import { useState, useEffect, useRef } from "react";
import PersonalCampaignsPanel from "@/components/dialer/PersonalCampaignsPanel";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import { getLeadScore } from "@/lib/getLeadScore";
import axios from "axios";
import { toast } from "sonner";
import { makeRCCall, sendRCSms } from "@/lib/rcIntegrationApi";
import {
  Phone, PhoneCall, PhoneOff, Mic, MicOff, Volume2, Pause, Play, SkipForward,
  Users, BarChart3, FileText, Sparkles, Clock, Target, Check, X, ChevronDown,
  ChevronRight, Plus, Search, AlertTriangle, Zap, Brain, MessageSquare,
  ArrowRight, ArrowRightLeft, Grid3X3, TrendingUp, Activity, Coffee, Star, ThumbsUp, ThumbsDown, Trash2, Loader2,
  ShieldCheck, BookOpen
} from "lucide-react";
import { CampaignGroupedQueue, InCallLeadPanel, MiniCampaignQueue, LeadFullEditorDrawer, DemoLeadFullView } from "./DialerLeadContext";
// Iter 178.12 (Danny Feb 2026) — Personal Campaign banner (System 1 MVP).
import PersonalCampaignBanner from "@/components/PersonalCampaignBanner";
import { PersonalCampaignEntry } from "@/components/PersonalCampaignEntry";
import { OverdueToggle, viewOf } from "@/components/dialer/OverdueToggle";
import { BackupSelector, useMyBackups } from "@/components/dialer/PcBackupSelector";
import { CampaignLearningPath, CampaignLearningPathV2, DialerEmailComposer, DialerTour, getLeadEmails } from "./DialerDemoExtensions";
import { AdminCampaignsPanel, AdminAgentsPanel, AdminAnalyticsPanel } from "./DialerAdminPanels";
import CampaignAudienceBuilder from "./CampaignAudienceBuilder";
import { HelpCircle, Mail } from "lucide-react";
import LiveCoachPanel from "@/components/dialer/LiveCoachPanel";
import FollowUpEmailModal from "@/components/dialer/FollowUpEmailModal";
import RCWebPhone from "@/components/rc/RCWebPhone";
import TalkTrackPanel from "@/components/TalkTrackPanel";
import TransferConferencePicker from "@/components/TransferConferencePicker";

/* ─── Constants ─── */
const SL = { fontSize: 10, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.4px" };

const CAMPAIGN_STATUS = { active: { bg: "#DAFBE8", color: "#1D9E75" }, paused: { bg: "#FFF3CD", color: "#856404" }, draft: { bg: "#f5f5f5", color: "#888" }, completed: { bg: "#E6F1FB", color: "#185FA5" } };

// ─── Slice C — pure filter function (exported for tests) ───
// All fields are optional; any supplied filter must match for a lead to be included.
export function applyCampaignFilters(leads, f = {}) {
  if (!Array.isArray(leads)) return [];
  const now = Date.now();
  const daysAgo = (d) => new Date(now - d * 86400000);
  const norm = (s) => String(s || "").toLowerCase().trim();
  // Iter 139 (Tatiana #59299037): suppress dialer queue noise.
  //   1) Drop Do-Not-Call leads entirely — TCPA + saves reps from
  //      re-calling the same dead numbers.
  //   2) Suppress leads that were just attempted within the last 4h
  //      with a non-connect outcome (no_answer / busy / voicemail).
  //      The 4h window matches industry best-practice "rest period"
  //      between attempts on the same number. Can be overridden by
  //      passing { skipFreshAttemptSuppression: true } in the filter
  //      object (used by the manual filter "Recently attempted" chip).
  const SUPPRESS_OUTCOMES = new Set(["no_answer", "busy", "voicemail"]);
  const SUPPRESS_MS = 4 * 60 * 60 * 1000;
  const skipSuppress = !!f.skipFreshAttemptSuppression;
  // Substring match helper — "ccm" matches "ccm and rpm", "rpm" matches "ccm and rpm"
  const tokenInHaystack = (token, hay) => {
    const t = norm(token);
    if (!t) return false;
    return hay.some(h => h === t || h.includes(t) || t.includes(h));
  };
  return leads.filter(l => {
    // Universal hard suppressions (run before any filter clause so DNC
    // leads NEVER show up in a queue, even when no filter is set).
    if (l && l.do_not_call) return false;
    if (!skipSuppress && l && l.last_call_outcome && SUPPRESS_OUTCOMES.has(l.last_call_outcome)) {
      const last = Date.parse(l.last_call_at || "");
      if (Number.isFinite(last) && now - last < SUPPRESS_MS) return false;
    }
    // Enrichment fallback lookups (NPPES + CMS).
    // We want AI-built campaigns to work over the entire lead pool —
    // not just leads that happen to have been enriched — so we read
    // BOTH the flat fields and the enrichment shape, treating missing
    // data as "unknown" rather than "doesn't match".
    const enr = (l && typeof l.enrichment === "object" && l.enrichment) || {};
    const nppes = enr.nppes || {};
    const cms = enr.cms || {};
    const pa = nppes.practice_address || {};

    // ── Lead status ────────────────────────────────────────────
    if (f.stages && f.stages.length && !f.stages.includes(l.stage)) return false;
    if (f.sub_statuses && f.sub_statuses.length) {
      // Wave 2 / A5 — segment-builder parity. Compare case-insensitively
      // against the stored `sub_status` to tolerate label drift.
      const ss = norm(l.sub_status);
      if (!ss || !f.sub_statuses.map(norm).some((x) => x === ss || ss.includes(x) || x.includes(ss))) return false;
    }
    if (f.ratings && f.ratings.length) {
      // Wave 2 / A5 — Hot/Warm/Cold parity. Fall back to score-band when
      // `rating` is missing (matches `tempLabel` logic in LeadSegments).
      const score = getLeadScore(l);
      const r = l.rating || (score >= 71 ? "Hot" : score >= 41 ? "Warm" : "Cold");
      if (!f.ratings.includes(r)) return false;
    }
    if (f.practice_types && f.practice_types.length) {
      // Wave 2 / A5 — substring match against any of the practice-type
      // shaped fields so legacy + enriched docs both qualify.
      const hay = [
        l.practice_type || "", l.practice_category || "", enr.practice_type || "",
      ].map(norm).filter(Boolean);
      if (!f.practice_types.some((p) => hay.some((h) => h.includes(norm(p)) || norm(p).includes(h)))) return false;
    }
    if (f.size_bands && f.size_bands.length) {
      // Wave 2 / A5 — match either a precomputed `size_band` field or a
      // band derived from group_size. Unknowns excluded only when the
      // user asked for a specific band (never silently include).
      const gs = Number(l.group_size || l.fte_count || l.practice_size || nppes.group_size || 0);
      const derived = !Number.isFinite(gs) || gs <= 0 ? "" :
        gs === 1 ? "Solo provider" :
        gs <= 5 ? "Small group (2–5)" :
        gs <= 25 ? "Multi-provider (6–25)" :
        gs <= 100 ? "Large group (26–100)" :
        "ACO / IDN (100+)";
      const band = l.size_band || derived;
      if (!band || !f.size_bands.includes(band)) return false;
    }
    if (f.nppes_taxonomy && f.nppes_taxonomy.length) {
      // Wave 2 / A5 — match against NPPES taxonomy codes anywhere in
      // the enrichment payload.
      const codes = [
        l.nppes_taxonomy_code || "",
        ...(Array.isArray(nppes.specialties) ? nppes.specialties.map((s) => s?.taxonomy || s?.code || s) : []),
        ...(Array.isArray(l.taxonomy_codes) ? l.taxonomy_codes : []),
      ].map((x) => String(x || "").toUpperCase().trim()).filter(Boolean);
      if (!f.nppes_taxonomy.some((t) => codes.some((c) => c.includes(String(t).toUpperCase().trim())))) return false;
    }
    if (f.tiers && f.tiers.length) {
      const t = norm(l.tier);
      if (!t || !f.tiers.some(x => norm(x) === t)) return false;
    }
    if (f.sources && f.sources.length) {
      const src = norm(l.lead_source || l.source).replace(/\s+/g, "_");
      if (!f.sources.some(x => norm(x).replace(/\s+/g, "_") === src || src.includes(norm(x).replace(/\s+/g, "_")))) return false;
    }
    if (f.product_interest && f.product_interest.length) {
      // Substring match so "ccm" hits "ccm and rpm" / "ccm only".
      const lp = [
        ...(Array.isArray(l.product_interest) ? l.product_interest : []),
        ...(Array.isArray(l.products) ? l.products : []),
        ...(Array.isArray(l.product_type) ? l.product_type : []),
        l.product_interest, l.products, l.product_type, // tolerate string form
      ].filter(Boolean).map(norm);
      if (!f.product_interest.some(p => tokenInHaystack(p, lp))) return false;
    }

    // ── NPPES / practice profile ───────────────────────────────
    if (f.specialty_groups && f.specialty_groups.length) {
      const hay = [
        l.specialty || "",
        ...(Array.isArray(l.specialty_multi) ? l.specialty_multi : []),
        ...((Array.isArray(l.contacts) ? l.contacts : []).flatMap(c => [c?.specialty, c?.specialty_group]).filter(Boolean)),
        nppes.primary_specialty || "",
        ...(Array.isArray(nppes.specialties) ? nppes.specialties.map(x => x?.taxonomy || x?.name || x) : []),
        cms.provider_specialty || "",
      ].map(norm).filter(Boolean);
      if (!f.specialty_groups.some(s => hay.some(h => h.includes(norm(s)) || norm(s).includes(h)))) return false;
    }
    if (f.group_size_min != null && f.group_size_min > 0) {
      const gs = Number(l.group_size || l.fte_count || l.practice_size || nppes.group_size || 0);
      if (!Number.isFinite(gs) || gs < f.group_size_min) return false;
    }
    if (f.group_size_max != null && f.group_size_max > 0 && f.group_size_max < 9999) {
      const gs = Number(l.group_size || l.fte_count || l.practice_size || nppes.group_size || NaN);
      // Unknown → include (don't penalise missing data).
      if (Number.isFinite(gs) && gs > f.group_size_max) return false;
    }
    if (f.years_min != null && f.years_min > 0) {
      const yrs = Number(l.years_in_practice || l.experience_years || 0);
      if (!Number.isFinite(yrs) || yrs < f.years_min) return false;
    }

    // ── Medicare + EHR signals ─────────────────────────────────
    // Read BOTH legacy flat (`l.medicare.*`) AND new enrichment shape
    // (`enrichment.cms.*`). Missing data is "unknown" — we EXCLUDE on
    // a hard `min > 0` (the user is asking for ≥N benes, unknowns can't
    // qualify) and INCLUDE on a `max` cap (small/unknown practices fit
    // "≤100 benes" campaigns).
    const bensVal = (l.medicare && l.medicare.beneficiaries != null)
      ? Number(l.medicare.beneficiaries)
      : (cms.beneficiaries != null ? Number(cms.beneficiaries) : NaN);
    if (f.medicare_min != null && f.medicare_min > 0) {
      if (!Number.isFinite(bensVal) || bensVal < f.medicare_min) return false;
    }
    if (f.medicare_max != null && f.medicare_max > 0 && f.medicare_max < 99999) {
      if (Number.isFinite(bensVal) && bensVal > f.medicare_max) return false;
    }
    const allowedVal = (l.medicare && l.medicare.allowed != null)
      ? Number(l.medicare.allowed)
      : (cms.allowed_amount != null ? Number(cms.allowed_amount) : NaN);
    if (f.medicare_allowed_min != null && f.medicare_allowed_min > 0) {
      if (!Number.isFinite(allowedVal) || allowedVal < f.medicare_allowed_min) return false;
    }
    if (f.medicare_allowed_max != null && f.medicare_allowed_max > 0) {
      if (Number.isFinite(allowedVal) && allowedVal > f.medicare_allowed_max) return false;
    }
    if (f.ehr_vendors && f.ehr_vendors.length) {
      const hay = [
        l.current_ehr || "", l.current_ehrs || "",
        l.ehr?.vendor || "", l.ehr?.product_name || "",
        (enr.chpl && (enr.chpl.vendor || enr.chpl.product_name)) || "",
      ].map(norm).filter(Boolean);
      if (!f.ehr_vendors.some(v => hay.some(h => h.includes(norm(v)) || norm(v).includes(h)))) return false;
    }
    if (f.signals && f.signals.length) {
      // Each token (e.g. "aco", "telehealth", "erx") must be TRUE.
      // Mirrors `LeadFilterPanel` semantics: "yes" = explicit true,
      // "unknown" never matches.
      const s = l.signals || {};
      const aco = l.aco_participation;
      const valFor = (id) => {
        if (id === "aco_participation" || id === "aco") return aco;
        return s[id];
      };
      if (!f.signals.every(id => valFor(id) === true)) return false;
    }

    // ── Pipeline scoring ───────────────────────────────────────
    if (typeof f.score_min === "number" && getLeadScore(l) < f.score_min) return false;
    if (typeof f.score_max === "number" && f.score_max < 100 && getLeadScore(l) > f.score_max) return false;
    if (f.value_min != null && (l.value || 0) < f.value_min) return false;
    if (f.value_max != null && f.value_max > 0 && f.value_max < 99999999) {
      // Wave 2 / A5 — pipeline value ceiling. Treat undefined value as 0.
      if ((l.value || 0) > f.value_max) return false;
    }
    if (f.owner === "me" && f.__currentUserId && l.owner_id !== f.__currentUserId) return false;

    // ── Geography ──────────────────────────────────────────────
    // Resolve state/city/zip from BOTH the flat fields AND the
    // NPPES practice address / CMS provider_state enrichment, so
    // geo filters work whenever ANY source has the data.
    const resolveState = () => {
      const candidates = [l.state, pa.state, cms.provider_state];
      for (const v of candidates) {
        const s = String(v || "").toUpperCase().trim();
        if (s) return s;
      }
      return "";
    };
    const resolveCity = () => {
      const c = norm(l.city || pa.city);
      return c;
    };
    const resolveZip = () => {
      // NPPES sometimes returns the full ZIP+4 ("208171841"), normalise to 5-digit.
      const raw = String(l.zip || l.zip_code || pa.zip || "").trim();
      if (!raw) return "";
      return raw.length >= 5 ? raw.slice(0, 5) : raw;
    };
    if (f.states && f.states.length) {
      const st = resolveState();
      if (!st || !f.states.map(x => String(x).toUpperCase().trim()).includes(st)) return false;
    }
    if (f.cities && f.cities.length) {
      const c = resolveCity();
      if (!c || !f.cities.some(x => norm(x) === c || c.includes(norm(x)))) return false;
    }
    if (f.zips && f.zips.length) {
      const z = resolveZip();
      if (!z || !f.zips.some(x => String(x).trim().slice(0, 5) === z || z.startsWith(String(x).trim()))) return false;
    }
    // Legacy `geography` field (substring match against state+region).
    if (f.geography && f.geography.length) {
      const g = norm(l.state || pa.state || l.region);
      if (!f.geography.map(norm).some(x => g.includes(x))) return false;
    }

    // ── Activity ───────────────────────────────────────────────
    if (f.activity && f.activity !== "any") {
      const last = l.last_activity_at ? new Date(l.last_activity_at) : null;
      if (f.activity === "active_30d" && (!last || last < daysAgo(30))) return false;
      if (f.activity === "no_activity_30d" && last && last >= daysAgo(30)) return false;
      if (f.activity === "no_activity_60d" && last && last >= daysAgo(60)) return false;
    }
    if (f.last_contact_days_min != null && f.last_contact_days_min > 0) {
      const last = l.last_activity_at || l.last_contact_date;
      if (last) {
        const days = (now - new Date(last).getTime()) / 86400000;
        if (days < f.last_contact_days_min) return false;
      }
      // Unknown last-contact → include (lead has likely been dormant).
    }
    if (f.last_contact_days_max != null && f.last_contact_days_max > 0 && f.last_contact_days_max < 9999) {
      // Wave 2 / A5 — match leads contacted at most N days ago. Missing
      // contact data → exclude (we don't know if they were contacted).
      const last = l.last_activity_at || l.last_contact_date;
      if (!last) return false;
      const days = (now - new Date(last).getTime()) / 86400000;
      if (days > f.last_contact_days_max) return false;
    }
    return true;
  });
}


// Each campaign is pinned to a Learning Path course_id so the agent-view
// Learning Path card can load the matching course/chapters directly from
// `/api/learning-paths/{course_id}`. The ids map 1:1 to the backend
// LEARNING_COURSES list: "emr-sales-toolkit", "ccm", "apcm", "rpm",
// "quick-reference".

const AGENT_STATUSES = { on_call: { label: "On call", color: "#ff3b30", bg: "#fff1f0" }, available: { label: "Available", color: "#1D9E75", bg: "#DAFBE8" }, wrap_up: { label: "Wrap-up", color: "#ff9500", bg: "#FFF3CD" }, break: { label: "Break", color: "#8e8e93", bg: "#f5f5f5" }, offline: { label: "Offline", color: "#ccc", bg: "#f9f9f9" } };


const DISPOSITIONS = [
  { id: "meeting_booked", label: "Meeting booked", color: "#1D9E75", icon: "calendar" },
  { id: "callback_requested", label: "Callback requested", color: "#2563eb", icon: "phone" },
  { id: "interested", label: "Interested — send info", color: "#0071e3", icon: "mail" },
  { id: "not_interested", label: "Not interested", color: "#ff9500", icon: "x" },
  { id: "no_answer", label: "No answer", color: "#8e8e93", icon: "phone-off" },
  { id: "voicemail", label: "Left voicemail", color: "#A78BFA", icon: "mic" },
  { id: "wrong_number", label: "Wrong number", color: "#ff3b30", icon: "x" },
  { id: "do_not_call", label: "Do not call", color: "#ff3b30", icon: "x" },
];

// P1 — dispositions that imply a follow-up is required (callback datetime + next step).
// Exported so tests can validate the gate without importing the component internals.
export const FOLLOWUP_REQUIRED_DISPOSITIONS = new Set([
  "callback_requested", "interested", "meeting_booked", "voicemail", "no_answer",
]);

// Terminal dispositions that close an origin task on submit. Non-terminal ones
// reschedule the task (push its due_date to the follow-up datetime).
export const TERMINAL_DISPOSITIONS = new Set([
  "meeting_booked", "not_interested", "do_not_call", "wrong_number",
]);

// Pure validation helper — exported for unit tests.
export function validatePostCallForm({ disposition, nextAction, dueDate }) {
  const errors = {};
  if (!disposition) errors.disposition = "Pick a call disposition";
  if (disposition && FOLLOWUP_REQUIRED_DISPOSITIONS.has(disposition)) {
    if (!nextAction || !nextAction.trim()) errors.nextAction = "Next step is required for this disposition";
    if (!dueDate) {
      errors.dueDate = "Follow-up date/time is required for this disposition";
    } else {
      // P1.5 — follow-up must be strictly in the future. `datetime-local`
      // value has no timezone; JS parses it as local time, which is correct
      // behavior for a rep scheduling in their own timezone.
      const when = new Date(dueDate);
      if (Number.isNaN(when.getTime())) {
        errors.dueDate = "Invalid follow-up date/time";
      } else if (when.getTime() <= Date.now()) {
        errors.dueDate = "Follow-up must be in the future";
      }
    }
  }
  return errors;
}

/* ─── DialerAddContactModal (Feature 3) ───
 * Quick top-toolbar modal to add a contact to the work queue.
 * - Demo mode  → appended to an in-memory buffer (no backend call)
 * - Real mode  → POST /api/leads (create_lead is open to any authenticated
 *   CRM user per existing RBAC); the work queue is refetched.
 * Fields: contact_name (req), phone (req), email, company (req), specialty
 */
function DialerAddContactModal({ onClose, onCreated }) {
  const { token } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const valid = name.trim() && phone.trim() && company.trim();

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    const snap = {
      id: `local-${Date.now()}`,
      name: name.trim(),
      practice: company.trim(),
      phone: phone.trim(),
      email: email.trim(),
      priority: "warm",
      reason: "Manually added via Add Contact",
      aiScore: 50,
      attempts: 0,
      campaign: "",
    };
    try {
      const res = await axios.post(`${API}/leads`, {
        contact_name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        company: company.trim(),
        title: company.trim(),
        specialty: specialty.trim() || undefined,
        stage: "new_lead",
        lead_source: "sales_agent",
      }, { headers: { Authorization: `Bearer ${token}` } });
      onCreated({ ...snap, id: res.data?.id || snap.id });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add contact — try again");
    }
    setSaving(false);
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.38)" }}
      data-testid="dialer-add-contact-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 440, background: "var(--surface, #fff)", borderRadius: 12, boxShadow: "0 24px 64px rgba(0,0,0,0.16)" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "0.5px solid var(--border, #eee)" }}>
          <div style={{ fontSize: 14, fontWeight: 650 }}>Add contact to queue</div>
          <button onClick={onClose} data-testid="dialer-add-contact-close" style={{ width: 28, height: 28, borderRadius: "50%", border: "0.5px solid var(--border, #eee)", background: "var(--surface, #fff)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <X style={{ width: 14, height: 14, color: "var(--text-tertiary, #888)" }} />
          </button>
        </div>
        <div style={{ padding: 20, display: "grid", gap: 12 }}>
          <div>
            <label style={{ ...SL, display: "block", marginBottom: 5 }}>Contact name *</label>
            <input
              data-testid="dialer-add-contact-name"
              value={name} onChange={e => setName(e.target.value)}
              placeholder="Dr. Jane Smith"
              style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ ...SL, display: "block", marginBottom: 5 }}>Phone *</label>
              <input
                data-testid="dialer-add-contact-phone"
                value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }}
              />
            </div>
            <div>
              <label style={{ ...SL, display: "block", marginBottom: 5 }}>Email</label>
              <input
                data-testid="dialer-add-contact-email"
                value={email} onChange={e => setEmail(e.target.value)}
                placeholder="jane@practice.com"
                style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }}
              />
            </div>
          </div>
          <div>
            <label style={{ ...SL, display: "block", marginBottom: 5 }}>Practice / company *</label>
            <input
              data-testid="dialer-add-contact-company"
              value={company} onChange={e => setCompany(e.target.value)}
              placeholder="Valley Urgent Care"
              style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label style={{ ...SL, display: "block", marginBottom: 5 }}>Specialty</label>
            <input
              data-testid="dialer-add-contact-specialty"
              value={specialty} onChange={e => setSpecialty(e.target.value)}
              placeholder="Urgent care, Family practice, Cardiology…"
              style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }}
            />
          </div>
        </div>
        <div style={{ padding: "14px 20px", borderTop: "0.5px solid var(--border, #eee)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "7px 16px", fontSize: 12, borderRadius: 8, border: "0.5px solid #ccc", background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!valid || saving}
            data-testid="dialer-add-contact-submit"
            style={{ padding: "7px 16px", fontSize: 12, fontWeight: 500, borderRadius: 8, border: "none", background: valid ? "#2563eb" : "#9CA3AF", color: "#fff", cursor: valid ? "pointer" : "default", fontFamily: "inherit", opacity: saving ? 0.65 : 1 }}
          >
            {saving ? "Adding…" : "Add to queue"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
/* ── Resume Last Call pill — surfaces when a previous /dialer/live session
 *    is still in flight in sessionStorage. One click jumps the rep back into
 *    the same lead. Cleared by SalesDialerLive on dispose/back. ───────── */
function ResumeLastCallPill() {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => {
    try {
      const raw = sessionStorage.getItem("dialerActiveSession");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  useEffect(() => {
    // Re-sync if the user comes back from another tab/path.
    const onFocus = () => {
      try {
        const raw = sessionStorage.getItem("dialerActiveSession");
        setSession(raw ? JSON.parse(raw) : null);
      } catch { /* ignore */ }
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onFocus);
    };
  }, []);

  if (!session?.leadId) return null;
  const label = session.contactName || session.leadName || "in-progress call";
  return (
    <button
      data-testid="resume-last-call-pill"
      onClick={() => navigate(`/dialer/live?leadId=${encodeURIComponent(session.leadId)}&resume=1`)}
      title={`Resume call with ${label}`}
      className="btn"
      style={{
        fontSize: 11, padding: "5px 12px", display: "inline-flex", alignItems: "center", gap: 6,
        background: "linear-gradient(135deg, #FFF7E6, #FFEFD6)", borderColor: "#F5A524", color: "#854F0B",
        fontWeight: 600,
      }}>
      <PhoneCall style={{ width: 12, height: 12 }} />
      Resume call · {label}
    </button>
  );
}

export default function SalesDialer() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = user?.role || "super_admin";
  const isAdmin = ["super_admin", "crm_admin", "sales_manager"].includes(role);

  // ───────── Real-mode data sources (Feb 2026 — fixes 3 user-reported
  //   bugs: "I activated agent Himangna but don't see her", "Unable to
  //   assign agents to finish campaign setup", "Next button not active").
  //
  //   Root cause: these three arrays were hardcoded to `[]` while the
  //   backend endpoints existed and worked. The wizard's Step 4 disables
  //   the Next button when `agents.length === 0` selected (line 2685),
  //   which made the whole campaign-creation flow impossible to complete.
  //
  //   Endpoints (all manager-gated for non-list reads, list endpoints
  //   visible to everyone with dialer access):
  //     GET /api/dialer/campaigns          — manager sees all; agent sees own + assigned
  //     GET /api/dialer/scripts            — manager sees all; agent sees own + published
  //     GET /api/dialer/assignable-users   — assignable as agent (super_admin/
  //                                          crm_admin/sales_manager/sales_agent/
  //                                          call_agent, not disabled)
  //
  //   The /assignable-users payload returns `full_name`/`email`/`role`;
  //   we normalize to `name`/`campaign`/`callsToday` for the wizard's
  //   render shape (lines 2611-2616).
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [scripts,   setScripts]   = useState([]);
  const [agents,    setAgents]    = useState([]);
  // Iter 167 (Bug #1, #4 — Danny report): canonical refresh for the main
  // `campaigns` state. Previously the campaign create/edit/start flows
  // called `loadQopsData?.()` (which only refreshes the Queue Ops tab
  // state) OR `onRefreshCampaigns?.()` (a no-op stub) — so PATCHes and
  // status flips happened on the backend but the UI showed stale data.
  // The user reported "Manage Agents save changes doesn't persist" and
  // "Start Campaign doesn't route" because of this stale view. Now every
  // mutation pipeline calls this single refresh which updates the main
  // list in place.
  const refreshCampaigns = async () => {
    if (!token) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/dialer/campaigns`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCampaigns(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Failed to refresh campaigns:", err?.response?.status, err?.message);
    }
  };
  useEffect(() => {
    if (!token) return;
    const API_PREFIX = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const headers = { Authorization: `Bearer ${token}` };
    let cancelled = false;
    (async () => {
      try {
        const [campRes, scrRes, agRes] = await Promise.all([
          axios.get(`${API_PREFIX}/dialer/campaigns`,        { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_PREFIX}/dialer/scripts`,          { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_PREFIX}/dialer/assignable-users`, { headers }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setCampaigns(Array.isArray(campRes.data) ? campRes.data : (campRes.data?.items || []));
        setScripts(Array.isArray(scrRes.data)    ? scrRes.data   : (scrRes.data?.items   || []));
        // Wizard's row template reads {name, role, campaign, callsToday}.
        // Map the safe projection from /assignable-users → wizard shape.
        // `campaign`/`callsToday` are best-effort optionals (no per-user
        // dialer telemetry endpoint yet — fall back to "" / 0).
        // Wizard's row template reads {name, role, campaign, callsToday}.
        // Map the safe projection from /assignable-users → wizard shape.
        // `campaign`/`callsToday` are best-effort optionals (no per-user
        // dialer telemetry endpoint yet — fall back to "" / 0).
        //
        // Iter 195.6 — sort by callsToday DESC so the /dialer Agents tab
        // surfaces the most-active reps first (matches how managers scan
        // the scoreboard). Ties break by name asc for stable ordering
        // across renders. Applied at the source so every consumer
        // (AdminAgentsPanel scoreboard, AdminCampaignsPanel pills,
        // AssignAgentsStep wizard) inherits the same order without
        // re-sorting downstream.
        //
        // Iter 195.8 — attach connected / meetings / avg_duration from
        // the backend so all four columns in the scoreboard show real
        // data (was placeholder zeroes for everything except calls).
        const rawAgents = Array.isArray(agRes.data) ? agRes.data : (agRes.data?.items || []);
        const fmtDur = (s) => {
          if (!s || s <= 0) return "—";
          const m = Math.floor(s / 60), sec = s % 60;
          return `${m}:${String(sec).padStart(2, "0")}`;
        };
        const mapped = rawAgents.map(u => ({
          id: u.id,
          name: u.full_name || u.email || "Unnamed",
          role: u.role,
          email: u.email,
          campaign: u.current_campaign || "",
          callsToday:    u.calls_today       || 0,
          connected:     u.connected_today   || 0,
          meetings:      u.meetings_today    || 0,
          avgDurationSec: u.avg_duration_today_sec || 0,
          avgDuration:   fmtDur(u.avg_duration_today_sec),
          status: u.status || "available",
        }));
        mapped.sort((a, b) => {
          const diff = (b.callsToday || 0) - (a.callsToday || 0);
          if (diff !== 0) return diff;
          return (a.name || "").localeCompare(b.name || "");
        });
        setAgents(mapped);
      } catch {
        // best-effort; the UI already renders empty-states for each list
      } finally {
        if (!cancelled) setCampaignsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);
  // Real-mode work queue: fetched from /api/dialer/queue. Demo mode keeps
  // Real-mode CRM queue, fetched from the backend. We store the queue as
  // state so claim/
  // outcome calls can re-fetch to refresh the list.
  const [realQueue, setRealQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  // Feature 3 — local buffer of contacts added via the top-toolbar
  // "Add Contact" modal. Demo mode prepends these so the new contact
  // appears at the top of the queue; real mode re-fetches /dialer/queue.
  const [demoAddedContacts, setDemoAddedContacts] = useState([]);

  const fetchRealQueue = async () => {
    if (!token) return;
    setQueueLoading(true);
    try {
      // Iter 275 (Jul 2026) — pass an explicit `limit: 10000` (matches
      // the backend ceiling in server.py `dialer_get_queue`). Without
      // this the request silently used the backend default of 5,000,
      // which truncated large campaigns like the 6,663-lead CCM Test
      // July 20th → the rep saw "Call Queue 5000 of 5000" while the
      // Agent view correctly reported 6,663 remaining. One request
      // fits in ~5 MB gzipped and the queue list is already
      // virtualized (react-window), so no rendering cost.
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dialer/queue`, {
        params: { limit: 10000 },
        headers: { Authorization: `Bearer ${token}` },
      });
      // Flatten snapshot onto the row so the existing card renderer keeps working.
      // Note: we expose `queue_item_id` separately and keep `id = lead_id` so
      // downstream code (startCall, makeRCCall, /dialer/post-call) keeps using
      // the lead id as the natural CRM identifier — the queue id is only
      // relevant for /claim and /outcome calls.
      const items = (res.data || []).map(row => ({
        id: row.lead_id,
        queue_item_id: row.id,
        queue_status: row.status,
        campaign_id: row.campaign_id,
        name: row.lead_snapshot?.name || "Lead",
        practice: row.lead_snapshot?.practice || "",
        phone: row.lead_snapshot?.phone || "",
        priority: row.lead_snapshot?.priority || "warm",
        reason: row.lead_snapshot?.reason || "",
        aiScore: getLeadScore(row.lead_snapshot),
        attempts: row.lead_snapshot?.attempts || 0,
        campaign: row.lead_snapshot?.campaign || row.campaign_name || "",
      }));
      setRealQueue(items);
    } catch (err) {
      // Silent fail — show empty queue; console log for dev visibility.
      // eslint-disable-next-line no-console
      console.warn("Failed to load work queue:", err?.response?.status, err?.message);
      setRealQueue([]);
    } finally {
      setQueueLoading(false);
    }
  };

  useEffect(() => {
    fetchRealQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const workQueue = realQueue;

  // Iter 167 (Bug #3 — Danny report): track which leads the rep has
  // already opened/called today. Hydrated from localStorage so reloads
  // preserve the marker. Visualized in CampaignGroupedQueue via the
  // `visitedLeadIds` prop — viewed rows render at 60% opacity with a
  // small "✓ Viewed" pill, preventing accidental re-dials.
  const [pcOverdue, setPcOverdue] = useState(false);
  const [pcCount, setPcCount] = useState(null);
  const [pcOwner, setPcOwner] = useState("");
  const [pcMyCount, setPcMyCount] = useState(null);
  const [visitedLeadIds, setVisitedLeadIds] = useState(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const raw = localStorage.getItem(`dialer_viewed_${today}`);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch (_) { return new Set(); }
  });

  // Iter 167.b — Dialer header KPI strip (calls today / connect rate /
  // meetings). Polls `/api/dialer/my-stats` every 60s so the rep sees
  // their progress without leaving the dialer. Hidden when there's no
  // data yet (e.g. first login of the day) to avoid showing zeros.
  const [myStats, setMyStats] = useState(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/dialer/my-stats`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setMyStats(r.data);
      } catch (_) { /* non-fatal */ }
    };
    fetchStats();
    const intervalId = setInterval(fetchStats, 60_000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [token]);

  const [view, setView] = useState(isAdmin ? "admin" : "agent");
  const [adminTab, setAdminTab] = useState("campaigns");
  // Feature 3 — Top-toolbar "Add Contact" quick-modal.
  const [showAddContact, setShowAddContact] = useState(false);
  // Admin Queue Ops panel (Scope A) — real-mode only, manager-gated.
  const [qopsCampaigns, setQopsCampaigns] = useState([]);      // [{id, name, status}]
  const [qopsUsers, setQopsUsers] = useState([]);              // [{id, full_name, email, role}]
  const [qopsCampaignId, setQopsCampaignId] = useState("");
  const [qopsSource, setQopsSource] = useState("filters");     // "filters" | "lead_ids"
  const [qopsFilters, setQopsFilters] = useState({ stages: [], interest_levels: [], ai_score_min: 0, lead_source: [] });
  const [qopsLeadIds, setQopsLeadIds] = useState("");
  const [qopsAssignees, setQopsAssignees] = useState([]);
  const [qopsRunning, setQopsRunning] = useState(false);
  const [qopsResult, setQopsResult] = useState(null);          // { generated, skipped_existing, assignees_used, total_candidates }
  const [qopsError, setQopsError] = useState(null);

  // Queue Ops (admin panel) — load campaigns + assignable users lazily when
  // the tab is opened. Real mode only, manager-gated by the outer render
  // condition; backend endpoints also enforce the same.
  const loadQopsData = async () => {
    if (!token) return;
    try {
      const [camps, users] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dialer/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dialer/assignable-users`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setQopsCampaigns((camps.data || []).map(c => ({ id: c.id, name: c.name, status: c.status })));
      setQopsUsers(users.data || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Failed to load Queue Ops data:", err?.response?.status, err?.message);
    }
  };

  const runQueueGeneration = async () => {
    if (!qopsCampaignId) { setQopsError("Pick a campaign first"); return; }
    setQopsRunning(true); setQopsError(null); setQopsResult(null);
    const payload = {};
    if (qopsSource === "lead_ids") {
      const ids = qopsLeadIds.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      if (ids.length === 0) {
        setQopsError("Provide at least one lead ID");
        setQopsRunning(false); return;
      }
      payload.lead_ids = ids;
    } else {
      const f = {};
      if (qopsFilters.stages?.length) f.stages = qopsFilters.stages;
      if (qopsFilters.interest_levels?.length) f.interest_levels = qopsFilters.interest_levels;
      if (qopsFilters.lead_source?.length) f.lead_source = qopsFilters.lead_source;
      if (qopsFilters.ai_score_min > 0) f.ai_score_min = qopsFilters.ai_score_min;
      if (Object.keys(f).length === 0) {
        setQopsError("Set at least one filter (stages, interest levels, score, or source)");
        setQopsRunning(false); return;
      }
      payload.filters = f;
    }
    if (qopsAssignees.length > 0) payload.assignees = qopsAssignees;
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/dialer/campaigns/${qopsCampaignId}/generate-queue`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setQopsResult(r.data);
      // Iter 276 (Jul 2026) — enrich the toast with the same
      // audience-vs-callable delta the agent path surfaces, so admins
      // running Queue Ops see exactly why the row count differs from
      // the campaign card's audience size.
      const d = r.data || {};
      const excluded = Number(d.excluded_dnc || 0) + Number(d.excluded_wrong_specialty || 0);
      if (excluded > 0) {
        const parts = [];
        if (d.excluded_dnc)              parts.push(`${Number(d.excluded_dnc).toLocaleString()} DNC`);
        if (d.excluded_wrong_specialty)  parts.push(`${Number(d.excluded_wrong_specialty).toLocaleString()} wrong-specialty`);
        toast.success(
          `Queue generated: ${d.generated} items (${d.skipped_existing} duplicates skipped, ${excluded.toLocaleString()} excluded — ${parts.join(", ")})`,
          { duration: 6000 },
        );
      } else {
        toast.success(`Queue generated: ${d.generated} items (${d.skipped_existing} duplicates skipped)`);
      }
      fetchRealQueue();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Generation failed";
      setQopsError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Queue generation failed");
    } finally {
      setQopsRunning(false);
    }
  };

  // Lazy-load campaigns + assignees when the Queue Ops tab is opened.
  useEffect(() => {
    if (isAdmin && adminTab === "queue_ops" && qopsCampaigns.length === 0 && qopsUsers.length === 0) {
      loadQopsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTab, isAdmin, token]);
  const [agentView, setAgentView] = useState("queue"); // queue | call | postcall
  // Re-check backup assignments each time the agent queue is shown (e.g. right after switching from Admin view).
  const pcBackups = useMyBackups(token, viewOf(pcOverdue), `${view}-${agentView}`);
  useEffect(() => {
    if (pcOwner && !pcBackups.some((b) => b.owner_id === pcOwner)) setPcOwner("");
  }, [pcBackups, pcOwner]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [callActive, setCallActive] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [lastCallTranscript, setLastCallTranscript] = useState([]);
  const [callTimer, setCallTimer] = useState(0);
  const [muted, setMuted] = useState(false);
  // Iter 185 (Feb 2026) — classic Sales Dialer Transfer/Conference pickers
  // and RC call id capture (for /calls/transfer + /calls/conference).
  const [classicXferOpen, setClassicXferOpen] = useState(false);
  const [classicConfOpen, setClassicConfOpen] = useState(false);
  const [classicCallId, setClassicCallId] = useState(null);
  useEffect(() => {
    const onStarted = (ev) => setClassicCallId(ev?.detail?.call_id || null);
    const onEnded = () => { setClassicCallId(null); setClassicXferOpen(false); setClassicConfOpen(false); };
    window.addEventListener("webphone:call-started", onStarted);
    window.addEventListener("webphone:call-ended", onEnded);
    return () => {
      window.removeEventListener("webphone:call-started", onStarted);
      window.removeEventListener("webphone:call-ended", onEnded);
    };
  }, []);
  const [selectedDisposition, setSelectedDisposition] = useState(null);
  const [callNotes, setCallNotes] = useState("");
  // Slice A — Quick note mini-log + save confirmation
  const [noteLog, setNoteLog] = useState([]);
  const [noteSavedFlash, setNoteSavedFlash] = useState(false);
  const notesTextareaRef = useRef(null);
  // Slice B — Active-call contact capture
  const [activeLead, setActiveLead] = useState(null);           // lead currently on the call
  const [contactCaptureOpen, setContactCaptureOpen] = useState(false);
  const [contactCaptureForm, setContactCaptureForm] = useState({ contact_name: "", role: "", phone: "", email: "" });
  const [newContactPrompt, setNewContactPrompt] = useState(null); // { name } shown inline after Save
  // Slice E — Post-call preview task box
  const [postCallPreview, setPostCallPreview] = useState(null);
  const [aiCopilotOpen, setAiCopilotOpen] = useState(false);
  const [sentiment, setSentiment] = useState("neutral");
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [campaignStep, setCampaignStep] = useState(1);
  // Prompt-2: Step 4 (Assign agents) + Step 5 (Review) state
  const [campaignStepAgents, setCampaignStepAgents] = useState([]); // list of agent ids
  const [campaignDistribution, setCampaignDistribution] = useState("round_robin");
  const [campaignDailyTarget, setCampaignDailyTarget] = useState(100);
  // Edit mode — pre-fills the modal and flips Launch → Save changes.
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [campaignNameInput, setCampaignNameInput] = useState("");
  const [campaignTypeInput, setCampaignTypeInput] = useState("outbound");
  const [campaignPriorityInput, setCampaignPriorityInput] = useState("normal");
  const [campaignScriptIdInput, setCampaignScriptIdInput] = useState("");
  // Reset modal state to a clean baseline (used by open/close + launch).
  const resetCampaignModalState = () => {
    setCampaignStep(1);
    setCampaignStepAgents([]);
    setCampaignDistribution("round_robin");
    setCampaignDailyTarget(100);
    setEditingCampaignId(null);
    setCampaignNameInput("");
    setCampaignTypeInput("outbound");
    setCampaignPriorityInput("normal");
    setCampaignScriptIdInput("");
    setCampaignFilters({
      stages: [], sources: [], product_interest: [],
      activity: "any", score_min: 0, score_max: 100,
      value_min: null, owner: "any", geography: [],
      states: [], cities: [], zips: [],
    });
    setActivePresetId(null);
    setLuminaPinIds(null);
    setLuminaPinMeta(null);
  };
  // Iter 270 (Jul 2026) — Escape-key dismiss for the New/Edit Campaign
  // wizard. Backdrop click-to-close was removed after reps kept losing
  // multi-step state to stray clicks; Escape remains as the fast
  // keyboard dismiss.
  useEffect(() => {
    if (!showCreateCampaign) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setShowCreateCampaign(false);
        resetCampaignModalState();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showCreateCampaign]);
  // Pre-fill every controlled field from an existing campaign and jump to
  // `step`. Called from the Campaigns tab "Edit"/"Manage agents"/"Change
  // script" quick actions.
  const openEditCampaign = (c, step = 1) => {
    if (!c) return;
    resetCampaignModalState();
    setEditingCampaignId(c.id);
    setCampaignNameInput(c.name || "");
    setCampaignTypeInput(c.type || "outbound");
    setCampaignPriorityInput(c.priority || "normal");
    setCampaignScriptIdInput(c.script_id || "");
    // Filters: tolerate both camel- and snake-case payloads.
    const f = c.filters || {};
    setCampaignFilters({
      stages: Array.isArray(f.stages) ? f.stages : [],
      sources: Array.isArray(f.sources || f.lead_source) ? (f.sources || f.lead_source) : [],
      product_interest: Array.isArray(f.product_interest || f.product_type) ? (f.product_interest || f.product_type) : [],
      activity: f.activity || "any",
      score_min: Number.isFinite(f.score_min) ? f.score_min : (Number.isFinite(f.ai_score_min) ? f.ai_score_min : 0),
      score_max: Number.isFinite(f.score_max) ? f.score_max : 100,
      value_min: f.value_min ?? null,
      owner: f.owner || "any",
      geography: Array.isArray(f.geography) ? f.geography : [],
      states: Array.isArray(f.states) ? f.states : [],
      cities: Array.isArray(f.cities) ? f.cities : [],
      zips: Array.isArray(f.zips) ? f.zips : [],
    });
    setCampaignStepAgents(Array.isArray(c.agent_ids) ? c.agent_ids : []);
    setCampaignDistribution(c.distribution_method || "round_robin");
    setCampaignDailyTarget(Number.isFinite(c.daily_target_per_agent) ? c.daily_target_per_agent : 100);
    setCampaignStep(step);
    setShowCreateCampaign(true);
  };
  // Iter 167.b (Danny report follow-up): focused "Save agents only" path —
  // when editing an existing campaign and the user is on Step 4 (Assign
  // Agents), they shouldn't have to click Next → Save just to update the
  // agent roster. This PATCHes ONLY `agent_ids` + `distribution_method` +
  // `daily_target_per_agent` and closes the modal. Reuses refreshCampaigns
  // so the parent list updates immediately (Bug #1 fix from Iter 167).
  const saveAgentAssignmentsOnly = async () => {
    if (!editingCampaignId) return;
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/dialer/campaigns/${editingCampaignId}`,
        {
          agent_ids: campaignStepAgents,
          distribution_method: campaignDistribution,
          daily_target_per_agent: campaignDailyTarget,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${campaignNameInput || "Campaign"} — ${campaignStepAgents.length} agent${campaignStepAgents.length === 1 ? "" : "s"} assigned`);
      setShowCreateCampaign(false);
      resetCampaignModalState();
      await refreshCampaigns();
      loadQopsData?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save agent assignments");
    }
  };

  // Save-or-launch handler for the Step-5 primary button.
  // Edit mode  → PATCH /dialer/campaigns/{id} in real mode; demo toasts only.
  // Create     → POST /api/dialer/campaigns in real mode; demo toasts only.
  const saveOrLaunchCampaign = async () => {
    if (editingCampaignId) {
      const payload = {
        name: campaignNameInput,
        type: campaignTypeInput,
        priority: campaignPriorityInput,
        script_id: campaignScriptIdInput || null,
        agent_ids: campaignStepAgents,
        distribution_method: campaignDistribution,
        daily_target_per_agent: campaignDailyTarget,
        // iter 138 lisa@ehiehr.com fix: persist the FULL filter shape, not
        // just the original 9 fields. The audience builder (Step 2)
        // surfaces 20+ filters including medicare buckets, specialty
        // groups, tiers, EHR vendors, signals — without these the
        // persisted campaign drops back to "no real criteria" → backend
        // queue generator finds 0 OR all leads.
        filters: {
          ...campaignFilters,
          ...(Array.isArray(luminaPinIds) && luminaPinIds.length
            ? { lumina_pinned_lead_ids: luminaPinIds.slice(0, 50000), lumina_meta: luminaPinMeta || null }
            : {}),
        },
      };
      try {
        await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/api/dialer/campaigns/${editingCampaignId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success(`${campaignNameInput} updated`);
        setShowCreateCampaign(false); resetCampaignModalState();
        // Iter 167 (Bug #1): refresh the main campaigns list so the
        // newly-saved values (agent_ids, filters, script) are visible
        // immediately. Also refresh Queue Ops state if the tab is open.
        await refreshCampaigns();
        loadQopsData?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to save campaign");
      }
      return;
    }
    // Create flow.
    if (!campaignNameInput || !campaignNameInput.trim()) {
      toast.error("Campaign name is required");
      return;
    }
    const createPayload = {
      name: campaignNameInput.trim(),
      type: campaignTypeInput || "outbound",
      priority: campaignPriorityInput || "normal",
      status: "active",
      script_id: campaignScriptIdInput || null,
      agent_ids: campaignStepAgents || [],
      distribution_method: campaignDistribution || "round_robin",
      daily_target_per_agent: campaignDailyTarget || 50,
      // Iter 166 (Josh — issue #6 peter@ehiehr.com): when this create flow
      // is launched from /lead-segments via `?from_segment=<id>`, send the
      // segment id to the backend so it can hydrate filters server-side if
      // the user didn't pick filters in the UI. Eliminates the
      // "audienceless campaign" class of bug (7-of-10 historical campaigns
      // were saved with empty filters because the segment context wasn't
      // propagated).
      from_segment_id: (() => {
        try {
          const sp = new URLSearchParams(window.location.search);
          return sp.get("from_segment") || null;
        } catch { return null; }
      })(),
      // iter 138 lisa@ehiehr.com fix — persist the full filter object so
      // backend queue generation sees the same audience the user saw in
      // Step-2 preview. (See edit-payload above for the rationale.)
      filters: {
        ...campaignFilters,
        // iter 139 (2026-05-25): when Lumina pinned an audience via
        // fulltext fallback (e.g. "COPD"), the structured filters
        // alone cannot reproduce that match. Persist the frozen ID
        // list inside the filters sub-doc so downstream queue
        // generation can use it as the authoritative audience.
        ...(Array.isArray(luminaPinIds) && luminaPinIds.length
          ? { lumina_pinned_lead_ids: luminaPinIds.slice(0, 50000), lumina_meta: luminaPinMeta || null }
          : {}),
      },
    };
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/dialer/campaigns`,
        createPayload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${r.data.name} launched ✓`);
      setShowCreateCampaign(false); resetCampaignModalState();
      // Iter 167 (Bug #1): refresh both the main campaigns list AND
      // the Queue Ops state so the new campaign appears everywhere.
      await refreshCampaigns();
      loadQopsData?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to launch campaign");
    }
  };

  // ── Slice C — Campaign Step 2 filter state + presets + live preview ──
  const [campaignFilters, setCampaignFilters] = useState({
    stages: [],            // e.g. ["discovery","demo","proposal"]
    sub_statuses: [],      // Wave 2 / A5 — segment-builder parity
    ratings: [],           // Wave 2 / A5 — Hot/Warm/Cold
    practice_types: [],    // Wave 2 / A5 — multi-select practice types
    size_bands: [],        // Wave 2 / A5 — Solo/Small/Multi/Large/ACO
    nppes_taxonomy: [],    // Wave 2 / A5 — taxonomy code chips
    sources: [],           // ["inbound","referral",...]
    product_interest: [],  // product ids
    activity: "any",       // "any" | "active_30d" | "no_activity_30d" | "no_activity_60d"
    score_min: 0,
    score_max: 100,
    value_min: null,       // pipeline value floor
    value_max: 99999999,   // Wave 2 / A5 — pipeline value ceiling
    owner: "any",          // "any" | "me"
    geography: [],         // free-tag array (states / regions)
    states: [],            // §3A — explicit US state codes (e.g. ["CA","NY"])
    cities: [],            // §3A — free-tag city names
    zips: [],              // §3A — free-tag ZIP codes (5 or 5+4)
    // AI-driven range filters — surfaced by /api/ai/segment-parse
    specialty_groups: [],
    tiers: [],
    ehr_vendors: [],
    signals: [],
    medicare_min: 0,
    medicare_max: 99999,
    medicare_allowed_min: 0,
    medicare_allowed_max: 0,
    group_size_min: 0,
    group_size_max: 9999,
    years_min: 0,
    last_contact_days_min: 0,
    last_contact_days_max: 9999, // Wave 2 / A5 — range complement
  });
  // Iter 280 (Jul 2026) — initialize with `empty: true` so the "define
  // your audience" placeholder is shown from the very first render of
  // Step 2 (before the useEffect runs). Prevents a fleeting "0 leads
  // match" flash on wizard open.
  const [campaignPreview, setCampaignPreview] = useState({ count: 0, sample: [], loading: false, empty: true });
  // Iter 139 (user bug 2026-05-25): When the AI brief panel parses
  // through Lumina (which can find leads via unstructured fulltext
  // fallback, e.g. "COPD"), the returned matched_ids supersede the
  // structured `applyCampaignFilters` evaluation. Without this lock
  // the audience preview shows 0 even though Lumina found leads.
  // Cleared whenever the user manually touches any filter chip/input.
  const [luminaPinIds, setLuminaPinIds] = useState(null);
  const [luminaPinMeta, setLuminaPinMeta] = useState(null);
  const [campaignPresets, setCampaignPresets] = useState([]); // loaded from backend or localStorage
  const [activePresetId, setActivePresetId] = useState(null);

  // ── Slice D — Script management UI state ──
  const [scriptsList, setScriptsList] = useState([]);          // loaded from /api/dialer/scripts
  const [scriptEditor, setScriptEditor] = useState(null);      // object being edited (or new)
  const [scriptPreviewId, setScriptPreviewId] = useState(null); // open preview drawer for this id
  const canEditScripts = ["super_admin", "crm_admin", "sales_manager"].includes(user?.role);
  const canDeleteScripts = ["super_admin", "crm_admin"].includes(user?.role);
  const [expandedSection, setExpandedSection] = useState(0);
  const [activeScriptIdx, setActiveScriptIdx] = useState(0);
  // ── Section 10 — AI-assisted script generation state ──
  const [aiScriptGen, setAiScriptGen] = useState({
    open: true, // expanded by default for new scripts
    campaign_type: "Outbound cold call",
    specialty: "",
    primary_product: "CCM",
    call_goal: "Book a demo",
    tone: "Consultative",
    loading: false,
    banner: null,
  });
  const [aiSectionImprove, setAiSectionImprove] = useState({}); // { [idx]: { suggestion, loading } }
  /* Detailed disposition fields (from spreadsheet) */
  const [dispSection, setDispSection] = useState("outcome");
  const [dispForm, setDispForm] = useState({
    interestLevel: "", sentiment: "neutral", decisionTimeline: "", budgetRange: "", pricingFit: "",
    practiceType: "", specialty: "", numProviders: "", currentEhr: "", painPoints: "",
    nextAction: "", nextActionOwner: "", dueDate: "", leadRating: 0, expectedClose: "",
    keyValueProp: "", integrationReqs: "", migrationNeeds: "", decisionMakers: false,
    featureInterest: "", engagementLevel: "",
  });
  // P1 — inline validation errors on post-call form (Scope A)
  const [dispErrors, setDispErrors] = useState({}); // { disposition?, nextAction?, dueDate? }
  const timerRef = useRef(null);

  // Call timer
  useEffect(() => {
    if (callActive) { timerRef.current = setInterval(() => setCallTimer(t => t + 1), 1000); }
    else { clearInterval(timerRef.current); }
    return () => clearInterval(timerRef.current);
  }, [callActive]);

  const fmtTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // ── DEMO-ONLY — in-call lead context, editing, Google verify, AI suggestions
  const [demoLeadOverrides, setDemoLeadOverrides] = useState({});
  const [leadEditorOpen, setLeadEditorOpen] = useState(false);
  const [googleVerifying, setGoogleVerifying] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  // P1.6 — Auto-apply AI field edits is ALWAYS ON in demo mode per product.
  // No user-facing toggle; the flush effect runs as soon as new suggestions
  // arrive so the lead profile mirrors the live transcript.
  const [autoApplyAi, setAutoApplyAi] = useState(true);
  // Inline Lead Full View (demo-only) — stays inside Sales Dialer so the user
  // returns to the call on close. Holds the lead id being viewed (null = hidden).
  const [fullViewLeadId, setFullViewLeadId] = useState(null);
  // Power Dial — when enabled AND a call is submitted, the next lead in the
  // same campaign is auto-dialed.
  const [powerDialOn, setPowerDialOn] = useState(() => {
    try { return sessionStorage.getItem("dialer_power_dial") === "1"; } catch (_) { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("dialer_power_dial", powerDialOn ? "1" : "0"); } catch (_) {}
  }, [powerDialOn]);

  // Demo-only — email composer + guided tour state. Tour is NOT auto-started;
  // the rep must click "Take the tour" in the header to open it.
  const [emailComposerLead, setEmailComposerLead] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [sentEmails, setSentEmails] = useState([]);

  // Helper — find the next lead in the same campaign (skipping the current).
  const findNextInCampaign = (currentLeadId, campaignId) => {
    if (!campaignId) return null;
    const rows = effectiveWorkQueue.filter(l => l.campaign_id === campaignId && l.id !== currentLeadId);
    return rows[0] || null;
  };

  const effectiveWorkQueue = workQueue.map(l => ({ ...l, ...(demoLeadOverrides[l.id] || {}) }));
  const effectiveSelectedLead = selectedLead ? { ...selectedLead, ...(demoLeadOverrides[selectedLead.id] || {}) } : null;
  const selectedLeadTimeline = [];

  const updateDemoLead = (updated) => {
    if (!updated?.id) return;
    setDemoLeadOverrides(prev => ({ ...prev, [updated.id]: { ...(prev[updated.id] || {}), ...updated } }));
    if (selectedLead?.id === updated.id) setSelectedLead(prev => ({ ...prev, ...updated }));
  };

  const applyAiSuggestion = (s) => {
    if (!effectiveSelectedLead) return;
    updateDemoLead({ id: effectiveSelectedLead.id, [s.field]: s.value });
    setAiSuggestions(prev => prev.filter(x => x.field !== s.field));
    toast.success(`AI applied: ${s.field}`);
  };
  const dismissAiSuggestion = (s) => {
    setAiSuggestions(prev => prev.filter(x => x.field !== s.field));
  };

  // Auto-apply flushes suggestions whenever they arrive (demo: always on).
  useEffect(() => {
    if (!autoApplyAi || !effectiveSelectedLead || aiSuggestions.length === 0) return;
    setDemoLeadOverrides(prev => {
      const next = { ...prev };
      const lid = effectiveSelectedLead.id;
      next[lid] = { ...(next[lid] || {}) };
      aiSuggestions.forEach(s => { next[lid][s.field] = s.value; });
      return next;
    });
    toast.info(`AI auto-applied ${aiSuggestions.length} field updates`);
    setAiSuggestions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSuggestions, autoApplyAi]);

  // Google Places/Maps MOCK — flips google_verified after a fake 900ms delay.
  const runGoogleVerify = () => {
    if (!effectiveSelectedLead) return;
    setGoogleVerifying(true);
    setTimeout(() => {
      updateDemoLead({
        id: effectiveSelectedLead.id,
        google_verified: true,
        website: effectiveSelectedLead.website || `${(effectiveSelectedLead.practice || "").toLowerCase().replace(/\s+/g, "")}.com`,
        address: effectiveSelectedLead.address || "Verified by Google Places (mock)",
      });
      setGoogleVerifying(false);
      toast.success("Google Places verification complete");
    }, 900);
  };

  // ── Slice A — save note endpoint call + mini-log + ⌘+S / Ctrl+S shortcut
  const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
  const authHdr = () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" });
  const saveNote = async () => {
    const text = callNotes.trim();
    if (!text || !selectedLead?.id) return;
    try {
      const r = await fetch(`${API_BASE}/dialer/notes`, {
        method: "POST", headers: authHdr(),
        body: JSON.stringify({ lead_id: selectedLead.id, text }),
      });
      if (r.ok) {
        const saved = await r.json();
        setNoteLog(prev => [saved, ...prev].slice(0, 10));
        setCallNotes("");
        setNoteSavedFlash(true);
        setTimeout(() => setNoteSavedFlash(false), 2000);
      }
    } catch (_) { /* best-effort */ }
  };

  // Fetch existing notes when entering a call
  useEffect(() => {
    if (!selectedLead?.id || agentView !== "call") return;
    fetch(`${API_BASE}/dialer/notes?lead_id=${selectedLead.id}&limit=10`, { headers: authHdr() })
      .then(r => r.ok ? r.json() : [])
      .then(rows => setNoteLog(Array.isArray(rows) ? rows : []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLead?.id, agentView]);

  // ⌘+S / Ctrl+S while notes textarea is focused
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "s" || e.key === "S")) {
        if (document.activeElement === notesTextareaRef.current) {
          e.preventDefault();
          saveNote();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callNotes, selectedLead?.id]);

  // ── Slice B — prefill capture form when entering a call
  useEffect(() => {
    if (!selectedLead) return;
    setContactCaptureForm({
      contact_name: selectedLead.contact_name || "",
      role: selectedLead.contact_role || "",
      phone: selectedLead.phone || "",
      email: selectedLead.email || "",
    });
    setContactCaptureOpen(false);
    setNewContactPrompt(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on selectedLead.id; setters read the rest of the lead fresh at call-time
  }, [selectedLead?.id]);

  // P1 — One-click Call from Daily Tasks (Scope B). When the Dialer is opened
  // with ?task_id=...&lead_id=...&phone=...&name=..., prefill selectedLead,
  // mark the origin task in_progress, jump straight to the Call screen, and
  // clear the query string so back/refresh doesn't re-launch a second time.
  //
  // Iter 179.9 (Tatiana #3dbe9163 Feb 2026): also stash WHERE to send the
  // rep after they submit their disposition. Before this fix the rep was
  // dumped on the dialer queue with no obvious way back to either the lead
  // or the tasks page they came from — leaving them to fight with the
  // browser back stack (which intermittently failed). We now expose an
  // explicit "Back to tasks" / "Back to lead" button on the post-call
  // screen + auto-navigate on submit when the call originated from Tasks.
  const [returnTarget, setReturnTarget] = useState(null); // { label, path }
  useEffect(() => {
    const taskId = searchParams.get("task_id");
    const leadId = searchParams.get("lead_id");
    const phone = searchParams.get("phone");
    const name = searchParams.get("name");
    if (!taskId && !leadId) return;
    // Mark origin task in_progress (best-effort; ownership enforced by backend).
    if (taskId && token) {
      axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}`,
        { status: "in_progress" },
        { headers: { Authorization: `Bearer ${token}` } },
      ).catch(() => { /* non-owner → silent */ });
    }
    // Iter 179.9 — capture the return target NOW (before we strip the
    // query string). If launched from a task, send them back to tasks;
    // otherwise to the lead they were calling.
    if (taskId) {
      setReturnTarget({ label: "Back to tasks", path: "/communications?tab=tasks" });
    } else if (leadId) {
      setReturnTarget({ label: "Back to lead", path: `/lead-focus/${leadId}` });
    }
    const prefill = {
      id: leadId || "",
      task_id: taskId || null,
      name: name || "Task",
      practice: "",
      phone: phone || "",
      priority: "warm",
      reason: "Launched from Daily Tasks",
      aiScore: 0,
      attempts: 0,
      campaign: "",
    };
    startCall(prefill);
    setView("agent");
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), token]);

  const saveContactCapture = async (opts = {}) => {
    if (!selectedLead?.id || !contactCaptureForm.contact_name.trim()) return;
    const payload = {
      lead_id: selectedLead.id,
      contact_name: contactCaptureForm.contact_name.trim(),
      role: contactCaptureForm.role,
      phone: contactCaptureForm.phone,
      email: contactCaptureForm.email,
      create_contact: !!opts.create_contact,
    };
    try {
      const r = await fetch(`${API_BASE}/dialer/contact-capture`, {
        method: "POST", headers: authHdr(), body: JSON.stringify(payload),
      });
      if (!r.ok) return;
      const res = await r.json();
      toast.success(res.is_new_contact && res.new_contact_id ? "Contact added to lead" : "Contact updated");
      // Reflect changes in the lead row so the banner reads correctly
      setSelectedLead(l => l ? { ...l, contact_name: payload.contact_name, contact_role: payload.role, phone: payload.phone, email: payload.email } : l);
      if (res.is_new_contact && !opts.create_contact) {
        setNewContactPrompt({ name: payload.contact_name });
      } else {
        setNewContactPrompt(null);
        setContactCaptureOpen(false);
      }
    } catch (_) { /* best-effort */ }
  };

  // ── Slice E — server-side task preview refresh when disposition/next_step changes
  useEffect(() => {
    if (!selectedLead?.id || !selectedDisposition) { setPostCallPreview(null); return; }
    const body = JSON.stringify({
      lead_id: selectedLead.id,
      disposition: selectedDisposition,
      next_step: dispForm.nextAction,
      custom_date: dispForm.dueDate || null,
    });
    fetch(`${API_BASE}/dialer/post-call/preview`, { method: "POST", headers: authHdr(), body })
      .then(r => r.ok ? r.json() : { preview: null })
      .then(d => setPostCallPreview(d?.preview || null))
      .catch(() => setPostCallPreview(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDisposition, dispForm.nextAction, dispForm.dueDate, selectedLead?.id]);


  const startCall = async (lead) => {
    // Iter 167 (Bug #3 — Danny report): mark this lead as "viewed today"
    // so reps don't accidentally reopen the same lead twice. Stored
    // per-day in localStorage so it resets at midnight. Visualized as a
    // muted opacity + "✓ Viewed" pill in the CampaignGroupedQueue.
    try {
      if (lead?.id) {
        const today = new Date().toISOString().slice(0, 10);
        const key = `dialer_viewed_${today}`;
        const raw = localStorage.getItem(key);
        const set = new Set(raw ? JSON.parse(raw) : []);
        set.add(lead.id);
        localStorage.setItem(key, JSON.stringify([...set]));
        setVisitedLeadIds(new Set(set));
      }
    } catch (_) { /* localStorage quota — best-effort */ }
    // The redesigned dialer page owns the live-call UX. Route there with the
    // selected lead's id so SalesDialerLive can preselect it from the queue.
    // Iter 194 (Vipal — Feb 2026): forward `campaign_id` so the live page
    // pulls the FULL campaign queue from /api/dialer/queue (was previously
    // ignoring the campaign and fetching `/api/leads?limit=50` — which is
    // why Lisa's 1,680-lead CCM-NY campaign showed only 47 leads).
    // The legacy in-page call view (CallActiveView) is retained for any
    // deep-linked admin tooling but is no longer the user-facing path.
    const _cid = lead?.campaign_id || lead?.queue_campaign_id || "";
    const _qs = new URLSearchParams();
    if (lead?.id) _qs.set("leadId", lead.id);
    if (_cid) _qs.set("campaign_id", _cid);
    navigate(`/dialer/live${_qs.toString() ? "?" + _qs.toString() : ""}`);
    // Real-mode: claim the queue item so no other rep picks it up. Silent
    // on conflicts — an already-claimed row (e.g. on reload) should not
    // block the rep from continuing the call.
    if (lead?.queue_item_id && lead.queue_status === "pending") {
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/dialer/queue/${lead.queue_item_id}/claim`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (_) { /* 409/403 → already claimed; continue */ }
    }
  };
  // Smart-fallback "Start calling" handler invoked from the Campaign Overview
  // header CTA. Prefer an existing queue item for the current user; otherwise
  // try to generate a queue for this campaign and start the first claimed lead.
  // Surfaces a role-specific toast when generation is forbidden (403).
  // Iter 175.5 (Danny — Tier 1 #5): now ALSO surfaces a meaningful error
  // when generate-queue 4xx's. Previously any error (including the very
  // common "you're not assigned to this campaign" 403) was silently
  // swallowed and the rep saw "No eligible leads" — which is misleading,
  // there ARE eligible leads, the rep just needs to be added as an agent.
  const startCampaignCall = async (campaign, leadsInCampaign) => {
    if (!campaign) return;
    // Real mode
    const mine = (realQueue || []).filter(l => l.campaign_id === campaign.id && l.queue_status !== "completed");
    if (mine.length > 0) { startCall(mine[0]); return; }
    let genRes = null;
    try {
      genRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/dialer/campaigns/${campaign.id}/generate-queue`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 403) {
        toast.error(`You're not assigned to "${campaign.name}". Ask an admin to add you, or open the campaign in Admin → Edit and add yourself to "Agents".`);
      } else if (status === 422 && /no resolved audience/i.test(String(detail))) {
        toast.error(`"${campaign.name}" has no resolved audience yet. Open Audience Builder and pick a segment.`);
      } else {
        toast.error(`Could not start "${campaign.name}": ${detail || e.message || "unknown error"}`);
      }
      return;
    }
    // Iter 276 (Jul 2026) — surface the audience vs. callable delta so
    // the rep sees "why did 6,663 audience shrink to 5,021 callable?"
    // at exactly the moment they'd notice it. Numbers come from the
    // /generate-queue response (`excluded_dnc`, `excluded_wrong_specialty`,
    // `total_candidates`).
    const gd = genRes?.data || {};
    const dncN = Number(gd.excluded_dnc || 0);
    const wrongN = Number(gd.excluded_wrong_specialty || 0);
    const totalExcluded = dncN + wrongN;
    if (totalExcluded > 0) {
      const parts = [];
      if (dncN)   parts.push(`${dncN.toLocaleString()} DNC`);
      if (wrongN) parts.push(`${wrongN.toLocaleString()} wrong-specialty`);
      toast.info(
        `"${campaign.name}" — ${Number(gd.total_candidates || 0).toLocaleString()} callable of ${Number((campaign.leads ?? campaign.total_leads ?? 0)).toLocaleString()} audience · ${totalExcluded.toLocaleString()} excluded (${parts.join(", ")})`,
        { duration: 6000 },
      );
    }
    await fetchRealQueue();
    try {
      // Iter 192p (Peter, June 25) — pass `campaign_id` so the backend
      // pre-filters server-side. Without this, the 500-row to_list cap
      // hid this campaign's items from any rep whose total queue
      // exceeded 500 (Peter had 5,905 open rows across 11 campaigns).
      // Iter 275 (Jul 2026) — also pass `limit: 10000` (backend
      // ceiling) so campaigns with more than 5,000 leads (default) get
      // their full queue returned in one shot instead of truncated.
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dialer/queue`, {
        params: { campaign_id: campaign.id, limit: 10000 },
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = res.data || [];
      if (items.length === 0) {
        // Iter 175.5 — clearer copy: distinguish "actually nothing to call"
        // (audience exhausted) from "you're not on the agent list".
        const onAgentList = (campaign.agent_ids || []).includes(currentUser?.id);
        toast.info(onAgentList
          ? `"${campaign.name}" — all leads have already been worked or claimed by other agents.`
          : `No leads queued for you on "${campaign.name}". You may not be assigned to this campaign yet.`);
        return;
      }
      const first = items[0];
      startCall({
        id: first.lead_id,
        queue_item_id: first.id,
        queue_status: first.status,
        campaign_id: first.campaign_id,
        name: first.lead_snapshot?.name || "Lead",
        practice: first.lead_snapshot?.practice || "",
        phone: first.lead_snapshot?.phone || "",
        priority: first.lead_snapshot?.priority || "warm",
        reason: first.lead_snapshot?.reason || "",
        aiScore: getLeadScore(first.lead_snapshot),
        attempts: first.lead_snapshot?.attempts || 0,
        campaign: first.lead_snapshot?.campaign || first.campaign_name || campaign.name,
      });
    } catch (err) {
      // This catches errors from the queue READ / startCall, not the
      // generate-queue POST (which has its own dedicated handler above).
      toast.error(err?.response?.data?.detail || "Failed to start campaign call");
    }
  };
  const endCall = () => {
    setCallActive(false);
    setAgentView("postcall");
    setDispSection("outcome");
    // P1.5 — AI auto-prefill for non-required hints only. `nextAction` and
    // `dueDate` are intentionally LEFT EMPTY so the rep must actively commit
    // a follow-up plan (validation gate enforces this on submit).
    setDispForm(prev => ({
      ...prev,
      sentiment: sentiment,
      interestLevel: sentiment === "positive" ? "High" : sentiment === "negative" ? "Low" : "Medium",
      engagementLevel: callTimer > 180 ? "High" : callTimer > 60 ? "Medium" : "Low",
      decisionTimeline: sentiment === "positive" ? "1-3 Months" : "Uncertain",
      nextActionOwner: user?.full_name || "",
      leadRating: sentiment === "positive" ? 4 : sentiment === "negative" ? 2 : 3,
    }));
  };
  const submitDisposition = async () => {
    // P1 — full validation (Scope A). For callback/follow-up dispositions, block
    // submit until nextAction + dueDate are filled. Surfaces inline errors, nav
    // to the Next Steps tab, and a single user-friendly toast.
    const errs = validatePostCallForm({
      disposition: selectedDisposition,
      nextAction: dispForm.nextAction,
      dueDate: dispForm.dueDate,
    });
    if (Object.keys(errs).length > 0) {
      setDispErrors(errs);
      if (errs.nextAction || errs.dueDate) setDispSection("next");
      toast.error(errs.disposition || errs.dueDate || errs.nextAction || "Fill required fields");
      return;
    }
    setDispErrors({});
    if (!selectedLead?.id) {
      toast.success(`Call logged: ${DISPOSITIONS.find(d => d.id === selectedDisposition)?.label}`);
      setSelectedLead(null); setSelectedDisposition(null); setCallNotes(""); setAgentView("queue"); setCallTimer(0);
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/dialer/post-call`, {
        method: "POST", headers: authHdr(),
        body: JSON.stringify({
          lead_id: selectedLead.id,
          disposition: selectedDisposition,
          next_step: dispForm.nextAction,
          notes: callNotes,
          custom_date: dispForm.dueDate || null,
          follow_up_at: dispForm.dueDate || null,
        }),
      });
      if (r.ok) {
        const res = await r.json();
        if (res.task_created) {
          toast.success(`Task created: ${res.task_title}`);
        } else {
          toast.success(`Call logged: ${DISPOSITIONS.find(d => d.id === selectedDisposition)?.label}`);
        }
        if (res.email_queued) toast.info("Follow-up email queued");
      }
    } catch (_) { toast.error("Could not save disposition"); }
    // Real-mode: close the queue item (silent on conflict). Refresh queue.
    if (selectedLead?.queue_item_id) {
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/dialer/queue/${selectedLead.queue_item_id}/outcome`,
          {
            disposition: selectedDisposition,
            notes: callNotes,
            next_step: dispForm.nextAction,
            follow_up_at: dispForm.dueDate || null,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (_) { /* already-completed → no-op */ }
      fetchRealQueue();
    }
    // P1 — if call was launched from a Tasks surface (task_id query param),
    // transition the origin task. Terminal dispositions → completed; non-terminal
    // follow-up dispositions → reschedule (push due_date to the selected follow-up).
    if (selectedLead?.task_id) {
      const isTerminal = TERMINAL_DISPOSITIONS.has(selectedDisposition);
      const patch = isTerminal
        ? { status: "completed" }
        : { status: "pending", due_date: dispForm.dueDate || null };
      try {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${selectedLead.task_id}`,
          patch,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success(isTerminal ? "Task completed" : "Task rescheduled");
      } catch (_) { /* owner-check or network → silently drop */ }
    }
    setSelectedLead(null); setSelectedDisposition(null); setCallNotes(""); setAgentView("queue"); setCallTimer(0);
    // Iter 179.9 (Tatiana #3dbe9163) — auto-route the rep BACK to where
    // they came from once the disposition is saved. This fixes the
    // "button to get back to the lead doesn't work until I refresh"
    // bug by short-circuiting the unreliable browser-back path with an
    // explicit React Router push. We use window.location.assign for
    // the cross-route hop so any stale React state on the dialer page
    // is fully reset.
    if (returnTarget?.path) {
      const dest = returnTarget.path;
      setReturnTarget(null);
      // Give the toast a beat to register before we navigate away.
      setTimeout(() => {
        try { window.location.assign(dest); } catch (_) { navigate(dest); }
      }, 450);
    }
  };

  // ─────────────── Slice C — Campaign filter helpers ───────────────
  const CAMPAIGN_PRESET_DEFAULTS = [
    { id: "preset_stage_discovery", label: "Discovery stage", filters: { stages: ["discovery"] } },
    { id: "preset_stage_proposal", label: "Proposal ready", filters: { stages: ["proposal","demo"] } },
    { id: "preset_lapsed_90", label: "Lapsed 90+ days", filters: { activity: "no_activity_60d" } },
    { id: "preset_hot_score", label: "Hot score (80+)", filters: { score_min: 80 } },
    { id: "preset_inbound_only", label: "Inbound leads", filters: { sources: ["inbound"] } },
    { id: "preset_mine", label: "My leads", filters: { owner: "me" } },
  ];
  const mergeFilters = (f) => ({
    stages: [], sources: [], product_interest: [], activity: "any",
    score_min: 0, score_max: 100, value_min: null, owner: "any", geography: [],
    states: [], cities: [], zips: [],
    specialty_groups: [], tiers: [], ehr_vendors: [], signals: [],
    medicare_min: 0, medicare_max: 99999,
    medicare_allowed_min: 0, medicare_allowed_max: 0,
    group_size_min: 0, group_size_max: 9999,
    years_min: 0, last_contact_days_min: 0,
    ...f,
  });
  const applyPreset = (preset) => {
    setCampaignFilters(mergeFilters(preset.filters));
    setActivePresetId(preset.id);
  };
  const toggleArrayFilter = (key, val) => {
    setActivePresetId(null);
    setLuminaPinIds(null); // manual edit → release the Lumina audience lock
    setCampaignFilters(f => {
      const cur = f[key] || [];
      return { ...f, [key]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
    });
  };
  const setFilter = (key, val) => { setActivePresetId(null); setLuminaPinIds(null); setCampaignFilters(f => ({ ...f, [key]: val })); };

  const savePresetLocally = () => {
    const name = prompt("Preset name:");
    if (!name) return;
    const p = { id: `user_${Date.now()}`, label: name, filters: campaignFilters };
    const next = [...campaignPresets, p];
    setCampaignPresets(next);
    try { localStorage.setItem("dialer_campaign_presets", JSON.stringify(next)); } catch (_) {}
    setActivePresetId(p.id);
    toast.success(`Saved preset "${name}"`);
  };
  useEffect(() => {
    try { const raw = localStorage.getItem("dialer_campaign_presets"); if (raw) setCampaignPresets(JSON.parse(raw)); } catch (_) {}
  }, []);

  // Debounced live preview — Iter 268 (Jul 2026): when LUMINA has
  // pinned a specific audience, fetch exactly those leads via the
  // server-side `/leads/search` endpoint with an `ids` whitelist.
  // Previously we fetched the first 5,000 leads (Iter 138) and did a
  // client-side set-intersection with the pin — but on tenants larger
  // than 5K, LUMINA-matched leads often sit beyond the window and the
  // preview showed 0 despite LUMINA reporting a match. Fetch-by-id
  // avoids the pagination-window drift entirely.
  //
  // Non-LUMINA campaigns still use the legacy fetch-first-5,000 path;
  // that path filters purely by structured campaignFilters, which
  // don't have the "matches scattered across all 29K leads" property
  // and behave correctly at 5K.
  useEffect(() => {
    if (!showCreateCampaign || campaignStep !== 2) return;
    // Iter 280 (Jul 2026) — empty-intent gate. Before this ship, opening
    // Step 2 always fetched /leads?limit=5000 and rendered whatever came
    // back ("4,977 leads match" with a random-looking sample). That
    // pre-populated preview looked like a pre-configured audience even
    // though the rep hadn't declared any intent yet. Now we suppress
    // the preview entirely until the rep expresses intent via:
    //   • the AI parse ➜ luminaPinIds !== null (even an empty pin
    //     counts — "matched 0" IS an intent)
    //   • picking a preset ➜ activePresetId !== null
    //   • toggling any filter ➜ campaignFilters diverges from defaults
    // The child renders a friendly "Define your audience" placeholder
    // in this state. Free perf win: no /leads fetch on Step 2 open.
    const cf = campaignFilters || {};
    const anyArrayNonEmpty = (
      (cf.stages?.length || cf.sub_statuses?.length || cf.ratings?.length ||
       cf.practice_types?.length || cf.size_bands?.length || cf.nppes_taxonomy?.length ||
       cf.sources?.length || cf.product_interest?.length || cf.geography?.length ||
       cf.states?.length || cf.cities?.length || cf.zips?.length ||
       cf.specialty_groups?.length || cf.tiers?.length ||
       cf.ehr_vendors?.length || cf.signals?.length) > 0
    );
    const anyScalarDiverged = (
      (cf.activity && cf.activity !== "any") ||
      (Number(cf.score_min) > 0) || (Number(cf.score_max) !== 100 && cf.score_max !== undefined) ||
      (cf.value_min !== null && cf.value_min !== undefined && Number(cf.value_min) > 0) ||
      (cf.value_max !== undefined && Number(cf.value_max) !== 99999999) ||
      (cf.owner && cf.owner !== "any") ||
      (Number(cf.medicare_min) > 0)           || (Number(cf.medicare_max) !== 99999 && cf.medicare_max !== undefined) ||
      (Number(cf.medicare_allowed_min) > 0)   || (Number(cf.medicare_allowed_max) > 0) ||
      (Number(cf.group_size_min) > 0)         || (Number(cf.group_size_max) !== 9999 && cf.group_size_max !== undefined) ||
      (Number(cf.years_min) > 0) ||
      (Number(cf.last_contact_days_min) > 0)  || (Number(cf.last_contact_days_max) !== 9999 && cf.last_contact_days_max !== undefined)
    );
    const hasActiveIntent = (
      luminaPinIds !== null ||
      activePresetId !== null ||
      anyArrayNonEmpty ||
      anyScalarDiverged
    );
    if (!hasActiveIntent) {
      setCampaignPreview({ count: 0, sample: [], loading: false, capped: false, luminaPinned: false, empty: true });
      return;
    }
    let cancelled = false;
    setCampaignPreview(p => ({ ...p, loading: true, empty: false }));
    const t = setTimeout(async () => {
      try {
        // ── LUMINA pin path — fetch exactly the matched leads.
        // Iter 271 (Jul 2026): an empty-but-non-null pin is a real
        // authoritative "0 leads" answer from LUMINA (e.g. "contacted
        // by <rep with no activity>"). Honor it by short-circuiting
        // to count=0 instead of falling back to unfiltered manual
        // filters — that fallback would show 2,731 unrelated leads
        // right next to the "Lumina matched 0" AI-panel message.
        if (Array.isArray(luminaPinIds)) {
          if (!luminaPinIds.length) {
            if (cancelled) return;
            setCampaignPreview({
              count: 0, sample: [], loading: false,
              capped: false, luminaPinned: true,
            });
            return;
          }
          const r = await fetch(`${API_BASE}/leads/search`, {
            method: "POST",
            headers: { ...authHdr(), "Content-Type": "application/json" },
            // excludeDnc:false so the preview shows the SAME count as
            // the LUMINA toast ("Lumina pinned N leads"). DNC filtering
            // is enforced at the actual call-dial stage anyway, so we
            // don't lose compliance by showing them in the preview.
            // Iter 272 (Jul 2026): send the full pin (up to LUMINA's
            // 10K backend ceiling) — the old 5,000 slice caused a UI
            // discrepancy where the AI panel said "matched 6,947
            // leads" but the audience preview beside it read only
            // 5,000 (production report on crm.ariaone.ai/dialer).
            body: JSON.stringify({ ids: luminaPinIds.slice(0, 10000), page_size: 500, excludeDnc: false }),
          });
          const data = r.ok ? await r.json() : { items: [], total: 0 };
          if (cancelled) return;
          const matches = data.items || [];
          // Authoritative count preference:
          //   1) LUMINA's own matched_count (already the truthful audience size
          //      the AI panel is showing) — this survives any /leads/search
          //      pagination or DNC edge cases.
          //   2) /leads/search total.
          //   3) row count fallback.
          const luminaAuthoritative = Number(luminaPinMeta?.matched_count);
          const authCount = Number.isFinite(luminaAuthoritative) && luminaAuthoritative >= 0
            ? luminaAuthoritative
            : (data.total || matches.length);
          setCampaignPreview({
            count: authCount,
            // Iter 281 (Jul 2026) — store the full fetched cache
            // (not sliced to 5). Child manages a `visibleCount` so
            // the rep can Load More without triggering an API call
            // until the cache is exhausted. Also enables the local
            // search box over the whole cache.
            sample: matches,
            loading: false,
            capped: !!luminaPinMeta?.ceiling_hit,
            luminaPinned: true,
          });
          return;
        }
        // ── Legacy path (no pin) — structured campaignFilters only.
        // Iter 138 lisa@ehiehr.com fix — bumped cap 500 → 5000 so the
        // rep isn't lied to about audience size.
        const FETCH_CAP = 5000;
        const r = await fetch(`${API_BASE}/leads?limit=${FETCH_CAP}`, { headers: authHdr() });
        const all = r.ok ? await r.json() : [];
        const matches = applyCampaignFilters(all, campaignFilters);
        if (cancelled) return;
        const cappedFetch = all.length >= FETCH_CAP;
        setCampaignPreview({
          count: matches.length,
          // Iter 281 (Jul 2026) — store the full filtered cache (up
          // to 5,000 leads). Child manages visibleCount + local
          // search over this cache. No slicing to 5 here.
          sample: matches,
          loading: false,
          capped: cappedFetch && matches.length >= FETCH_CAP,
          luminaPinned: false,
        });
      } catch (_) {
        if (!cancelled) setCampaignPreview({ count: 0, sample: [], loading: false, capped: false, luminaPinned: false });
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignFilters, campaignStep, showCreateCampaign, luminaPinIds, activePresetId]);

  // Iter 281 (Jul 2026) — Load-more helper for the audience preview.
  // Called by CampaignAudienceBuilder when the rep clicks "Load N more"
  // and the client-side cache has been exhausted (`visibleCount >=
  // sample.length` and `sample.length < count`). Only meaningful on
  // the LUMINA-pin path — the legacy path already caches up to 5,000
  // filtered rows client-side, so the button just increments
  // visibleCount without needing a fetch.
  //
  // Signature: () => Promise<void>. Never throws — errors surface as
  // a toast and the preview stays as-is.
  const loadMoreAudiencePage = async () => {
    if (!Array.isArray(luminaPinIds)) return;
    const cached = campaignPreview?.sample || [];
    const total  = Number(campaignPreview?.count || 0);
    if (cached.length >= total) return;   // nothing to fetch
    if (cached.length >= 5000) {
      toast.info("5,000 leads previewed. The remaining audience is applied when you Start Calling.");
      return;
    }
    // Send the next 500 IDs the client hasn't fetched yet.
    const nextIdSlice = luminaPinIds.slice(cached.length, cached.length + 500);
    if (!nextIdSlice.length) return;
    try {
      const r = await fetch(`${API_BASE}/leads/search`, {
        method: "POST",
        headers: { ...authHdr(), "Content-Type": "application/json" },
        body: JSON.stringify({ ids: nextIdSlice, page_size: 500, excludeDnc: false }),
      });
      const data = r.ok ? await r.json() : { items: [] };
      const newRows = data.items || [];
      if (!newRows.length) return;
      setCampaignPreview((prev) => {
        // Guard against dupes if the rep clicks in quick succession
        // while a fetch is in flight — merge by id.
        const seen = new Set((prev?.sample || []).map((l) => l.id));
        const merged = [...(prev?.sample || [])];
        for (const row of newRows) {
          if (!seen.has(row.id)) {
            seen.add(row.id);
            merged.push(row);
          }
        }
        return { ...prev, sample: merged };
      });
    } catch (e) {
      toast.error("Could not load more leads — try again.");
    }
  };
  // ─────────────── Slice D — Script API helpers ───────────────
  const loadScripts = async () => {
    try {
      const r = await fetch(`${API_BASE}/dialer/scripts`, { headers: authHdr() });
      if (r.ok) setScriptsList(await r.json());
    } catch (_) {}
  };
  useEffect(() => { if (adminTab === "scripts" || adminTab === "campaigns") loadScripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTab]);

  const saveScript = async (payload) => {
    const url = payload.id ? `${API_BASE}/dialer/scripts/${payload.id}` : `${API_BASE}/dialer/scripts`;
    const method = payload.id ? "PUT" : "POST";
    try {
      const r = await fetch(url, { method, headers: authHdr(), body: JSON.stringify(payload) });
      if (r.ok) { toast.success(payload.id ? "Script updated" : "Script created"); setScriptEditor(null); loadScripts(); }
      else if (r.status === 403) toast.error("Only managers or admins can edit scripts");
      else toast.error("Save failed");
    } catch (_) { toast.error("Network error"); }
  };
  const publishScript = async (id) => {
    const r = await fetch(`${API_BASE}/dialer/scripts/${id}/publish`, { method: "POST", headers: authHdr() });
    if (r.ok) { toast.success("Script published"); loadScripts(); }
    else if (r.status === 403) toast.error("Only managers or admins can publish");
  };
  const duplicateScript = async (id) => {
    const r = await fetch(`${API_BASE}/dialer/scripts/${id}/duplicate`, { method: "POST", headers: authHdr() });
    if (r.ok) { toast.success("Script duplicated"); loadScripts(); }
    else if (r.status === 403) toast.error("Not permitted");
  };
  const deleteScript = async (id) => {
    if (!confirm("Delete this script?")) return;
    const r = await fetch(`${API_BASE}/dialer/scripts/${id}`, { method: "DELETE", headers: authHdr() });
    if (r.ok) { toast.success("Script deleted"); loadScripts(); }
    else if (r.status === 403) toast.error("Only admins can delete");
  };

  // ── Section 10A — AI-generate full script ──
  const generateScriptWithAI = async () => {
    if (!scriptEditor) return;
    // CMS hard gate (mirrors aiTargeting.js)
    const SPECIALIST_GROUPS = new Set(["Cardiology","Pulmonology","Nephrology","Endocrinology","Oncology","Gastroenterology","Rheumatology","Neurology"]);
    let product = aiScriptGen.primary_product;
    let banner = null;
    if (["APCM","AWV"].includes(product) && SPECIALIST_GROUPS.has(aiScriptGen.specialty)) {
      banner = `${product} is CMS-ineligible for ${aiScriptGen.specialty}. Switching to CCM.`;
      product = "CCM";
      setAiScriptGen(s => ({ ...s, primary_product: "CCM", banner }));
    }
    setAiScriptGen(s => ({ ...s, loading: true }));
    try {
      const r = await fetch(`${API_BASE}/ai/dialer-script-generate`, {
        method: "POST", headers: { ...authHdr(), "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign_type: aiScriptGen.campaign_type,
          specialties: aiScriptGen.specialty ? [aiScriptGen.specialty] : [],
          primary_product: product,
          call_goal: aiScriptGen.call_goal,
          tone: aiScriptGen.tone,
        }),
      });
      if (!r.ok) throw new Error("Generation failed");
      const data = await r.json();
      // Map AI sections (bullets array) into the editor's section shape
      const sections = (data.sections || []).map(s => ({ title: s.title || "Section", bullets: Array.isArray(s.bullets) ? s.bullets : [String(s.text || "")] }));
      setScriptEditor(prev => ({
        ...prev,
        name: prev?.name || data.name || `${product} — ${aiScriptGen.call_goal}`,
        products: prev?.products?.length ? prev.products : [product.toLowerCase()],
        sections,
      }));
      setAiScriptGen(s => ({ ...s, loading: false, banner: data.banner_note || banner, open: false }));
      toast.success("AI generated this script — review and publish when ready");
    } catch (e) {
      setAiScriptGen(s => ({ ...s, loading: false }));
      toast.error("AI generation failed");
    }
  };

  // ── Section 10B — AI improve a single section ──
  const improveSectionWithAI = async (idx) => {
    const sec = scriptEditor?.sections?.[idx];
    if (!sec) return;
    setAiSectionImprove(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), loading: true, suggestion: null } }));
    try {
      const r = await fetch(`${API_BASE}/ai/dialer-script-improve`, {
        method: "POST", headers: { ...authHdr(), "Content-Type": "application/json" },
        body: JSON.stringify({
          section_title: sec.title,
          current_text: (sec.bullets || []).join("\n"),
          primary_product: aiScriptGen.primary_product,
          specialty: aiScriptGen.specialty,
          tone: aiScriptGen.tone,
        }),
      });
      if (!r.ok) throw new Error("improve failed");
      const data = await r.json();
      setAiSectionImprove(prev => ({ ...prev, [idx]: { loading: false, suggestion: data.suggestion || "" } }));
    } catch (e) {
      setAiSectionImprove(prev => ({ ...prev, [idx]: { loading: false, suggestion: null } }));
      toast.error("AI improvement failed");
    }
  };

  const acceptSectionImprovement = (idx) => {
    const sug = aiSectionImprove[idx]?.suggestion;
    if (!sug) return;
    const bullets = sug.split("\n").map(s => s.replace(/^[-•·*]\s*/, "").trim()).filter(Boolean);
    const next = [...(scriptEditor.sections || [])];
    next[idx] = { ...next[idx], bullets };
    setScriptEditor({ ...scriptEditor, sections: next });
    setAiSectionImprove(prev => ({ ...prev, [idx]: { loading: false, suggestion: null } }));
    toast.success("Section updated with AI suggestion");
  };


  return (
    <div data-testid="sales-dialer-page" data-mode="real">
      <div className="page-header">
        <div>
          <h1 className="page-title">Sales Dialer</h1>
          <p className="page-subtitle">{view === "admin" ? "Campaign management, scripts, agents, and analytics" : "Your calling work queue and active sessions"}</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Iter 167.b — Today's KPI strip. Only renders when at least
              one call has been made so we don't shame reps with 0/0/0
              at the start of the day. */}
          {myStats && myStats.calls > 0 && (
            <div
              data-testid="dialer-my-stats"
              title={`Today since 00:00 UTC · ${myStats.calls} calls · ${myStats.connect_rate}% connect · ${myStats.meetings_booked} meetings booked`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "5px 11px",
                background: "#0F2942", color: "#fff",
                borderRadius: 999, fontSize: 11, fontWeight: 500,
                fontFamily: "var(--font)", marginRight: 6,
              }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Phone style={{ width: 11, height: 11, color: "#93C5FD" }} />
                <span data-testid="dialer-my-stats-calls" style={{ fontWeight: 700 }}>{myStats.calls}</span>
                <span style={{ opacity: 0.7 }}>calls</span>
              </span>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span
                  data-testid="dialer-my-stats-connect"
                  style={{ fontWeight: 700, color: myStats.connect_rate >= 30 ? "#34c759" : myStats.connect_rate >= 15 ? "#FACC15" : "#fca5a5" }}>
                  {myStats.connect_rate}%
                </span>
                <span style={{ opacity: 0.7 }}>connect</span>
              </span>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span data-testid="dialer-my-stats-meetings" style={{ fontWeight: 700, color: "#A78BFA" }}>{myStats.meetings_booked}</span>
                <span style={{ opacity: 0.7 }}>meeting{myStats.meetings_booked === 1 ? "" : "s"}</span>
              </span>
            </div>
          )}
          {/* Feature 3 — Quick Add Contact (visible in both Admin + Agent views) */}
          <button
            data-testid="dialer-add-contact-btn"
            onClick={() => setShowAddContact(true)}
            className="btn btn-primary"
            style={{ fontSize: 11, padding: "5px 12px", display: "inline-flex", alignItems: "center", gap: 6 }}
            title="Add a new contact to the work queue"
          >
            <Plus style={{ width: 12, height: 12 }} /> Add Contact
          </button>
          <ResumeLastCallPill />
          <div style={{ display: "flex", background: "var(--gray-light)", borderRadius: 8, padding: 2 }} data-testid="admin-agent-toggle">
            {[{ id: "admin", label: isAdmin ? "Admin" : "Scripts" },{ id: "agent", label: isAdmin ? "Agent View" : "My Queue" }].map(v => (
              <button key={v.id} onClick={() => { setView(v.id); if (v.id === "agent") setAgentView("queue"); if (v.id === "admin" && !isAdmin) setAdminTab("scripts"); }}
                style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, fontWeight: view === v.id ? 600 : 400, background: view === v.id ? "#fff" : "transparent", color: view === v.id ? "var(--text-primary)" : "var(--text-tertiary)", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}
                data-testid={`dialer-view-${v.id}`}>{v.label}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="page-content">

        {/* ═══════════════ ADMIN VIEW ═══════════════ */}
        {view === "admin" && (<>
          <div style={{ display: "flex", gap: 0, borderBottom: "0.5px solid var(--border)", marginBottom: 16 }}>
            {[{ id: "campaigns", label: "Campaigns", icon: Target, adminOnly: true },{ id: "personal_campaigns", label: "Personal Campaigns", icon: Users, adminOnly: true, superOnly: true },{ id: "scripts", label: "Scripts", icon: FileText, adminOnly: false },{ id: "agents", label: "Agents", icon: Users, adminOnly: true },{ id: "queue_ops", label: "Queue Ops", icon: Zap, adminOnly: true },{ id: "analytics", label: "Analytics", icon: BarChart3, adminOnly: true }].filter(t => (isAdmin || !t.adminOnly) && (!t.superOnly || user?.role === "super_admin")).map(t => (
              <button key={t.id} onClick={() => setAdminTab(t.id)} data-testid={`admin-tab-${t.id}`}
                style={{ padding: "10px 20px", fontSize: 12, fontWeight: adminTab === t.id ? 600 : 400, cursor: "pointer", background: "none", border: "none", fontFamily: "var(--font)", color: adminTab === t.id ? "var(--blue)" : "var(--text-tertiary)", borderBottom: adminTab === t.id ? "2px solid var(--blue)" : "2px solid transparent", display: "flex", alignItems: "center", gap: 5 }}>
                <t.icon style={{ width: 13, height: 13 }} /> {t.label}
              </button>
            ))}
          </div>

          {/* Campaigns Tab (3.2) */}
          {adminTab === "campaigns" && (
            <div>
              {/* Iter 178.6 (Danny Feb 2026) — old top-of-page KPI strip
                  removed. AdminCampaignsPanel now renders its own KPI
                  cards scoped to the currently visible campaigns
                  (Active / Completed / Retarget / Archived bucket +
                  rep filter), which is what Danny's reference
                  screenshot called for. Only the "New Campaign" CTA
                  stays at the top right. */}
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 10 }}>
                <button onClick={() => { resetCampaignModalState(); setShowCreateCampaign(true); }} className="btn btn-primary" style={{ fontSize: 12, whiteSpace: "nowrap" }} data-testid="create-campaign-btn"><Plus style={{ width: 12, height: 12 }} /> New Campaign</button>
              </div>
              {campaignsLoading && (
                <div data-testid="campaigns-loading" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      data-testid={`campaign-skeleton-${i}`}
                      style={{
                        background: "#fff",
                        borderRadius: 10,
                        border: "0.5px solid #eee",
                        borderLeft: "4px solid #e5e7eb",
                        padding: "14px 18px",
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                      }}
                    >
                      <div className="skeleton" style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0 }} />
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div className="skeleton" style={{ width: 160, height: 14, borderRadius: 4 }} />
                          <div className="skeleton" style={{ width: 52, height: 16, borderRadius: 10 }} />
                          <div className="skeleton" style={{ width: 64, height: 16, borderRadius: 10 }} />
                        </div>
                        <div className="skeleton" style={{ width: 220, height: 10, borderRadius: 4 }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!campaignsLoading && campaigns.length === 0 && (
                <div data-testid="campaigns-empty" style={{ padding: "32px 20px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 12, border: "1px dashed var(--border)", borderRadius: 10 }}>
                  No campaigns yet. Click <strong>New Campaign</strong> to create your first one.
                </div>
              )}
              <AdminCampaignsPanel
                campaigns={campaigns}
                scripts={[...scriptsList, ...scripts]}
                agents={agents}
                token={token}
                onRefreshCampaigns={refreshCampaigns}
                onOpenEdit={openEditCampaign}
                onNavigateToLeads={(cid) => {
                  // iter 138 lisa@ehiehr.com fix — "View Leads" used to navigate
                  // to /leads?campaign=ID which the Leads page silently
                  // ignores → user sees all 11k+ leads (no filter applied).
                  // Now we read the campaign's persisted filters and
                  // encode the canonical Leads URL params so the
                  // /leads page applies the SAME audience the campaign
                  // was built for.
                  const c = (campaigns || []).find((x) => x.id === cid);
                  const f = (c && c.filters) || {};
                  const sp = new URLSearchParams();
                  // Mirror the filter keys that /leads' filterUrlSchema knows
                  // about (lib/leadFilterConfig + LeadFilterPanel canon).
                  const arr = (k, alt) => Array.isArray(f[k]) ? f[k] : (alt && Array.isArray(f[alt]) ? f[alt] : []);
                  const setArr = (urlKey, vals) => { if (vals && vals.length) sp.set(urlKey, vals.join(",")); };
                  setArr("sources", arr("sources", "lead_source"));
                  setArr("specialties", arr("specialty_groups", "specialties"));
                  setArr("products", arr("product_interest", "products"));
                  setArr("ehrs", arr("ehr_vendors", "ehrs"));
                  setArr("ratings", arr("tiers", "ratings"));
                  setArr("states", arr("states"));
                  // Geography legacy field (free-text region/state list)
                  setArr("regions", arr("geography"));
                  // Persist campaign id so /leads can label the chip too.
                  sp.set("campaign", cid);
                  navigate(`/leads?${sp.toString()}`);
                }}
                onSwitchToAnalytics={() => setAdminTab("analytics")}
              />
            </div>
          )}

          {/* Scripts Tab (3.3) */}
          {adminTab === "personal_campaigns" && user?.role === "super_admin" && (
            <PersonalCampaignsPanel token={token} />
          )}

          {adminTab === "scripts" && (
            <div>
              {/* Iter 176 Phase 5 (Danny — scoring_and_dialer_updates.md §5)
                  — Quick-reference talk-track panel with 7 mini categories
                  of vetted B2B healthcare cold-calling sound bites. */}
              <TalkTrackPanel />
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>{scriptsList.length + scripts.length} scripts in library</div>
                {canEditScripts && (
                  <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => setScriptEditor({ name: "", products: [], angles: [], estimated_minutes: 10, sections: [{ title: "Intro", bullets: ["Hi, this is {{rep_name}}…"] }], status: "draft" })} data-testid="new-script-btn">
                    <Plus style={{ width: 12, height: 12 }} /> New Script
                  </button>
                )}
              </div>
              {/* Iter 183 (Feb 2026) — publish-visibility hint for managers.
                  Reps only see scripts marked "Published" via /dialer/scripts;
                  drafts stay hidden. Prevents future "Talk Track not visible
                  to my reps" reports when someone forgets to click Publish. */}
              {canEditScripts && (scriptsList.length + scripts.length) > 0 && (
                <div
                  data-testid="scripts-publish-hint"
                  style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 12, padding: "8px 12px", background: "#F5F9FF", border: "1px solid #DBE7FA", borderRadius: 6, display: "flex", alignItems: "center", gap: 6 }}
                >
                  <FileText style={{ width: 12, height: 12, color: "#2563eb" }} />
                  Reps only see scripts marked <strong>&nbsp;Published</strong>&nbsp;— drafts remain manager-only.
                </div>
              )}
              {scriptsList.length === 0 && scripts.length === 0 && (
                <div data-testid="scripts-empty" style={{ padding: "32px 20px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 12, border: "1px dashed var(--border)", borderRadius: 10 }}>
                  No scripts yet. Click <strong>New Script</strong> to build your first call script.
                </div>
              )}
              {/* Real scripts from API */}
              {scriptsList.map(s => {
                // Prompt 2 §3 — derive perf + campaign-usage metadata.
                const usedInCount = campaigns.filter(c => c.script_id === s.id).length;
                const connectRate = s.connect_rate ?? s.metrics?.connect_rate;
                const meetingsBooked = s.meetings ?? s.metrics?.meetings ?? 0;
                const avgDuration = s.avg_duration_label || s.metrics?.avg_duration_label;
                const isDraft = (s.status || "draft") !== "published";
                const perfColor = connectRate >= 35 ? "#1D9E75" : connectRate >= 25 ? "#BA7517" : "#E24B4A";
                return (
                <div key={s.id} className="card" style={{ padding: "14px 18px", marginBottom: 8 }} data-testid={`script-${s.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</div>
                        <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase",
                          background: s.status === "published" ? "#D1FAE5" : "#FEF3C7",
                          color: s.status === "published" ? "#065F46" : "#92400E"
                        }} data-testid={`script-status-${s.id}`}>{s.status || "draft"}</span>
                      </div>
                      <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                        {(s.products || []).map(t => <span key={t} style={{ padding: "1px 6px", borderRadius: 10, fontSize: 9, background: "#EEEDFE", color: "#3C3489", textTransform: "uppercase" }}>{t}</span>)}
                        {(s.angles || []).map(t => <span key={t} style={{ padding: "1px 6px", borderRadius: 10, fontSize: 9, background: "#DBEAFE", color: "#1D4ED8" }}>{t}</span>)}
                        {s.estimated_minutes ? <span style={{ padding: "1px 6px", borderRadius: 10, fontSize: 9, background: "#f5f5f5", color: "#555" }}>{s.estimated_minutes}m</span> : null}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }} data-testid={`script-actions-${s.id}`}>
                      <button onClick={() => setScriptPreviewId(s.id)} className="btn" style={{ fontSize: 10, padding: "3px 8px" }} data-testid={`script-preview-${s.id}`}>Preview</button>
                      {canEditScripts && (<>
                        <button onClick={() => setScriptEditor(s)} className="btn" style={{ fontSize: 10, padding: "3px 8px" }} data-testid={`script-edit-${s.id}`}>Edit</button>
                        <button onClick={() => duplicateScript(s.id)} className="btn" style={{ fontSize: 10, padding: "3px 8px" }} data-testid={`script-duplicate-${s.id}`}>Duplicate</button>
                        {s.status !== "published" && <button onClick={() => publishScript(s.id)} className="btn btn-primary" style={{ fontSize: 10, padding: "3px 8px" }} data-testid={`script-publish-${s.id}`}>Publish</button>}
                      </>)}
                      {canDeleteScripts && <button onClick={() => deleteScript(s.id)} className="btn" style={{ fontSize: 10, padding: "3px 8px", color: "#A32D2D" }} data-testid={`script-delete-${s.id}`}>Delete</button>}
                    </div>
                  </div>
                  {/* Section preview chips — Prompt 2 §3B */}
                  <div data-testid={`script-sections-${s.id}`} style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                    {(s.sections || []).map((sec, i) => {
                      const isPub = s.status === "published";
                      return (
                        <span key={i} title={typeof sec === "string" ? sec : (sec.body || sec.script || "").slice(0, 200)}
                          data-testid={`script-section-chip-${s.id}-${i}`}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 12, fontSize: 11, background: isPub ? "#E6F1FB" : "#f5f5f5", color: isPub ? "#185FA5" : "#888" }}>
                          <span style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>{i + 1}</span>
                          {sec.title || (typeof sec === "string" ? sec : "Section")}
                        </span>
                      );
                    })}
                  </div>
                  {/* Perf stats row — Prompt 2 §3C */}
                  <div data-testid={`script-perf-${s.id}`} style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, padding: "8px 0", borderTop: "0.5px solid #eee", fontSize: 11 }}>
                    <div>
                      <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase" }}>Used</div>
                      <div style={{ fontWeight: 700 }}>{s.usage_count ?? s.usageCount ?? 0} <span style={{ fontSize: 10, color: "#888", fontWeight: 400 }}>times</span></div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase" }}>Human connect rate</div>
                      <div style={{ fontWeight: 700, color: connectRate != null ? perfColor : "#888" }}>{connectRate != null ? `${connectRate}%` : "—"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase" }}>Meetings</div>
                      <div style={{ fontWeight: 700, color: "#1D9E75" }}>{meetingsBooked}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase" }}>Avg call</div>
                      <div style={{ fontWeight: 700 }}>{avgDuration || "—"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase" }}>Campaigns</div>
                      <div style={{ fontWeight: 700, color: usedInCount > 0 ? "#185FA5" : "#888" }}>{usedInCount}</div>
                    </div>
                  </div>
                  {/* Draft warning — Prompt 2 §3D */}
                  {isDraft && (
                    <div data-testid={`script-draft-warning-${s.id}`} style={{ marginTop: 6, fontSize: 11, color: "#92400E", background: "#FFFBEA", border: "0.5px solid #F7D27B", borderRadius: 6, padding: "6px 10px" }}>
                      Draft — Not yet live for agents. Publish to activate in real calls.
                    </div>
                  )}
                  {!isDraft && usedInCount > 0 && (
                    <div data-testid={`script-campaign-note-${s.id}`} style={{ marginTop: 6, fontSize: 11, color: "#185FA5" }}>
                      Used in {usedInCount} active campaign{usedInCount === 1 ? "" : "s"}.
                    </div>
                  )}
                </div>
                );
              })}
              {/* Legacy showcase scripts (demo only) */}
              {scripts.map(s => (
                <div key={s.id} className="card" style={{ padding: "14px 18px", marginBottom: 8, opacity: 0.6 }} data-testid={`script-legacy-${s.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name} <span style={{ fontSize: 9, color: "#888", fontWeight: 400 }}>(demo)</span></div>
                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>{(s.tags || []).map(t => <span key={t} style={{ padding: "1px 6px", borderRadius: 10, fontSize: 9, background: "#EEEDFE", color: "#3C3489" }}>{t}</span>)}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Used {s.usageCount} times</div>
                      <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>Updated {s.lastUpdated}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Agents Tab (3.4) — Prompt 2 redesign */}
          {adminTab === "agents" && (
            <AdminAgentsPanel
              agents={agents}
              campaigns={campaigns}
              token={token}
              onOpenInvite={() => navigate("/settings?tab=team&invite=sales_agent")}
              onRefresh={() => { /* real-mode refetch owned by parent effect */ }}
            />
          )}

          {/* Analytics Tab (3.5) — Prompt 2 redesign */}
          {adminTab === "analytics" && (
            <AdminAnalyticsPanel
              campaigns={campaigns}
              agents={agents}
              token={token}
            />
          )}
          {/* Queue Ops Tab — real-mode only, manager-gated (Scope A) */}
          {adminTab === "queue_ops" && isAdmin && (
            <div data-testid="queue-ops-panel">
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Generate Work Queue</div>
                <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
                  Materialize dialer items for a campaign and round-robin distribute them to your team.
                </div>
              </div>

              <div className="card" style={{ padding: 18, marginBottom: 14 }}>
                {/* Campaign picker */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Campaign</label>
                  <select
                    data-testid="qops-campaign-select"
                    value={qopsCampaignId}
                    onChange={(e) => setQopsCampaignId(e.target.value)}
                    style={{ width: "100%", marginTop: 4, padding: "8px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6, fontFamily: "var(--font)" }}
                  >
                    <option value="">Select a campaign…</option>
                    {qopsCampaigns.map(c => (
                      <option key={c.id} value={c.id}>{c.name} {c.status ? `· ${c.status}` : ""}</option>
                    ))}
                  </select>
                </div>

                {/* Source toggle */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Generation source</label>
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    {[{ id: "filters", label: "Campaign filters" }, { id: "lead_ids", label: "Explicit lead IDs" }].map(s => (
                      <button
                        key={s.id}
                        data-testid={`qops-source-${s.id}`}
                        onClick={() => setQopsSource(s.id)}
                        className={qopsSource === s.id ? "btn btn-primary" : "btn"}
                        style={{ fontSize: 12 }}
                      >{s.label}</button>
                    ))}
                  </div>
                </div>

                {/* Source inputs */}
                {qopsSource === "filters" && (
                  <div data-testid="qops-filters-section" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                    <div>
                      <label style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Stages (comma-separated)</label>
                      <input
                        data-testid="qops-filter-stages"
                        value={(qopsFilters.stages || []).join(",")}
                        onChange={(e) => setQopsFilters({ ...qopsFilters, stages: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                        placeholder="e.g. discovery,demo,proposal"
                        style={{ width: "100%", marginTop: 4, padding: "8px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6, fontFamily: "var(--font)" }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Interest levels</label>
                      <input
                        data-testid="qops-filter-interest"
                        value={(qopsFilters.interest_levels || []).join(",")}
                        onChange={(e) => setQopsFilters({ ...qopsFilters, interest_levels: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                        placeholder="e.g. hot,warm"
                        style={{ width: "100%", marginTop: 4, padding: "8px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6, fontFamily: "var(--font)" }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Min AI score</label>
                      <input
                        type="number" min={0} max={100}
                        data-testid="qops-filter-score"
                        value={qopsFilters.ai_score_min || 0}
                        onChange={(e) => setQopsFilters({ ...qopsFilters, ai_score_min: parseInt(e.target.value || "0", 10) })}
                        style={{ width: "100%", marginTop: 4, padding: "8px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6, fontFamily: "var(--font)" }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Lead sources</label>
                      <input
                        data-testid="qops-filter-source"
                        value={(qopsFilters.lead_source || []).join(",")}
                        onChange={(e) => setQopsFilters({ ...qopsFilters, lead_source: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                        placeholder="e.g. inbound,referral"
                        style={{ width: "100%", marginTop: 4, padding: "8px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6, fontFamily: "var(--font)" }}
                      />
                    </div>
                  </div>
                )}

                {qopsSource === "lead_ids" && (
                  <div data-testid="qops-lead-ids-section" style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Lead IDs (comma or newline separated)</label>
                    <textarea
                      data-testid="qops-lead-ids-input"
                      rows={4}
                      value={qopsLeadIds}
                      onChange={(e) => setQopsLeadIds(e.target.value)}
                      placeholder="uuid-1, uuid-2, uuid-3"
                      style={{ width: "100%", marginTop: 4, padding: "8px 10px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, fontFamily: "monospace" }}
                    />
                  </div>
                )}

                {/* Assignee multi-select */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                    Assignees <span style={{ color: "#aaa", fontWeight: 400, textTransform: "none" }}>· round-robin</span>
                  </label>
                  <div data-testid="qops-assignees" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                    {qopsUsers.length === 0 && (
                      <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Loading team…</span>
                    )}
                    {qopsUsers.map(u => {
                      const picked = qopsAssignees.includes(u.id);
                      return (
                        <button
                          key={u.id}
                          data-testid={`qops-assignee-${u.id}`}
                          onClick={() => setQopsAssignees(picked ? qopsAssignees.filter(x => x !== u.id) : [...qopsAssignees, u.id])}
                          className={picked ? "btn btn-primary" : "btn"}
                          style={{ fontSize: 11, padding: "4px 10px" }}
                        >
                          {u.full_name || u.email} · {u.role}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 4 }}>
                    {qopsAssignees.length === 0 ? "If empty, queue will be assigned to you." : `${qopsAssignees.length} selected`}
                  </div>
                </div>

                {/* Run button */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <button
                    data-testid="qops-run-btn"
                    onClick={runQueueGeneration}
                    disabled={qopsRunning || !qopsCampaignId}
                    className="btn btn-primary"
                    style={{ fontSize: 13, opacity: qopsRunning || !qopsCampaignId ? 0.5 : 1 }}
                  >
                    {qopsRunning ? (<><Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> Generating…</>) : (<><Zap style={{ width: 12, height: 12 }} /> Generate Queue</>)}
                  </button>
                  {qopsError && (
                    <span data-testid="qops-error" style={{ fontSize: 12, color: "#ff3b30" }}>{qopsError}</span>
                  )}
                </div>
              </div>

              {/* Result summary */}
              {qopsResult && (
                <div className="card" data-testid="qops-result" style={{ padding: 18, borderLeft: "3px solid var(--blue)" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Run summary</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                    {[
                      { label: "Generated", val: qopsResult.generated, color: "#1D9E75", testid: "qops-result-generated" },
                      { label: "Skipped (duplicates)", val: qopsResult.skipped_existing, color: "#ff9500", testid: "qops-result-skipped" },
                      { label: "Total candidates", val: qopsResult.total_candidates, color: "#2563eb", testid: "qops-result-total" },
                      { label: "Assignees used", val: (qopsResult.assignees_used || []).length, color: "#A78BFA", testid: "qops-result-assignees" },
                    ].map(m => (
                      <div key={m.label} data-testid={m.testid} style={{ padding: "10px 12px", background: "#f9f9fb", borderRadius: 6 }}>
                        <div style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>{m.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: m.color, marginTop: 2 }}>{m.val}</div>
                      </div>
                    ))}
                  </div>
                  {qopsResult.assignees_used && qopsResult.assignees_used.length > 0 && (
                    <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-tertiary)" }}>
                      Round-robin distribution across: {qopsResult.assignees_used.map(id => {
                        const u = qopsUsers.find(x => x.id === id);
                        return u ? (u.full_name || u.email) : id.slice(0, 8);
                      }).join(" · ")}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </>)}
        {view === "agent" && (<>

          {/* Work Queue (3.6) */}
          {agentView === "queue" && (
            <div>
              <div style={{ background: "#0F2942", borderRadius: 10, padding: "14px 18px", marginBottom: 16 }} data-testid="ai-morning-briefing">
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                  <Sparkles style={{ width: 12, height: 12, color: "#93C5FD" }} />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}>Morning briefing</span>
                  <span style={{ fontSize: 10, color: "#8BA5C0", marginLeft: "auto" }}>AI-prioritized</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 6, flex: 1 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#34c759", marginTop: 5, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#CBD5E0" }}><strong style={{ color: "#fff" }}>{effectiveWorkQueue.length} leads</strong> in your queue</span>
                  </div>
                </div>
              </div>

              <CampaignGroupedQueue
                campaigns={campaigns}
                workQueue={effectiveWorkQueue}
                loading={campaignsLoading || queueLoading}
                visitedLeadIds={visitedLeadIds}
                personalBanner={
                  <PersonalCampaignBanner
                    view={viewOf(pcOverdue)}
                    ownerId={pcOwner || null}
                    onPickLead={(lead) =>
                      // Iter 194 (Vipal — Feb 2026): clicking a lead in
                      // the personal-campaign banner now opens the live
                      // dialer with the personal queue loaded, instead
                      // of bouncing to the lead-focus page. The rep can
                      // dial immediately without one extra click.
                      navigate(`/dialer/live?source=personal&view=${viewOf(pcOverdue)}${pcOwner ? `&owner=${pcOwner}` : ""}&leadId=${encodeURIComponent(lead.id)}`)
                    }
                  />
                }
                personalCampaignEntry={
                  <div data-testid="personal-campaign-section" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <BackupSelector backups={pcBackups} value={pcOwner} onChange={setPcOwner} myCount={pcMyCount} />
                    <OverdueToggle checked={pcOverdue} onChange={setPcOverdue} count={pcCount} />
                    <PersonalCampaignEntry
                      apiBase={process.env.REACT_APP_BACKEND_URL}
                      authToken={token}
                      reloadKey={campaigns}
                      view={viewOf(pcOverdue)}
                      ownerId={pcOwner || null}
                      onCount={(n) => { setPcCount(n); if (!pcOwner && n != null) setPcMyCount(n); }}
                      onStart={() => navigate(`/dialer/live?source=personal_live&view=${viewOf(pcOverdue)}${pcOwner ? `&owner=${pcOwner}` : ""}`)}
                    />
                  </div>
                }
                onStartCall={startCall}
                onStartCampaign={startCampaignCall}
                onOpenFullLead={(lead) => {
                  navigate(`/lead-focus/${lead.id}`);
                }}
                apiBase={process.env.REACT_APP_BACKEND_URL}
                authToken={token}
                onNavigateToResources={({ courseId, chapterId }) => {
                  const qs = new URLSearchParams({ tab: "learning", course: courseId });
                  if (chapterId) qs.set("chapter", chapterId);
                  navigate(`/resources?${qs.toString()}`);
                }}
              />
            </div>
          )}

          {/* Active Call Screen (3.7) */}
          {agentView === "call" && selectedLead && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14 }}>
              <div>
                {/* Call Header — Section 2A: name + sub-line + right-aligned timer; sentiment pills auto-updated (AI) */}
                <div className="card" style={{ padding: "16px 20px", background: callActive ? "#0F2942" : "#fff", marginBottom: 0, borderRadius: "10px 10px 0 0" }} data-testid="active-call-header">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 44, height: 44, borderRadius: "50%", background: callActive ? "rgba(255,59,48,0.2)" : "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {callActive ? <PhoneCall style={{ width: 20, height: 20, color: "#ff3b30", animation: "pulse 1.5s ease-in-out infinite" }} /> : <Phone style={{ width: 20, height: 20, color: "#2563eb" }} />}
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: callActive ? "#fff" : "var(--text-primary)" }}>
                          {(effectiveSelectedLead || selectedLead).name}
                          {(effectiveSelectedLead || selectedLead).google_verified && (
                            <span data-testid="call-header-google-badge" title="Google-verified" style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 8, padding: "2px 6px", borderRadius: 10, fontSize: 9, background: "#E8F0FE", color: "#1967D2", fontWeight: 600 }}>
                              <ShieldCheck style={{ width: 10, height: 10 }} /> Google
                            </span>
                          )}
                        </div>
                        {/* Sub-line per spec 2A */}
                        <div data-testid="call-header-subline" style={{ fontSize: 11, color: callActive ? "#8BA5C0" : "var(--text-tertiary)", marginTop: 2 }}>
                          {[
                            (effectiveSelectedLead || selectedLead).title,
                            (effectiveSelectedLead || selectedLead).practice,
                            (effectiveSelectedLead || selectedLead).phone,
                            (effectiveSelectedLead || selectedLead).reason,
                          ].filter(Boolean).join(" · ")}
                        </div>
                        {/* Iter 158 (Josh, Cluster G) — pre-call flag pills
                            so reps see critical context (Hospital Affiliated,
                            Language Barrier, Not Interested, etc.) BEFORE
                            connecting the call. Issues #998f5bcc, #19b4c55f,
                            #9219e213, #cd75f03d, #fd8e2601 all asked for
                            this exact pre-call surface. */}
                        {(() => {
                          const lead = effectiveSelectedLead || selectedLead;
                          const tags = Array.isArray(lead.lead_tag) ? lead.lead_tag : [];
                          const SURFACE = {
                            "Hospital Affiliated": { bg: "#fef3c7", fg: "#92400e", border: "#fcd34d", note: "Can't pitch products" },
                            "Language Barrier":    { bg: "#dbeafe", fg: "#1e40af", border: "#93c5fd", note: "Translator needed" },
                            "Not Interested":      { bg: "#fee2e2", fg: "#7f1d1d", border: "#fecaca", note: "Previously declined" },
                            "Deceased":            { bg: "#e5e7eb", fg: "#374151", border: "#9ca3af", note: "Provider deceased" },
                            "Retired":             { bg: "#e5e7eb", fg: "#374151", border: "#9ca3af", note: "Provider retired" },
                            "Closed Practice":     { bg: "#e5e7eb", fg: "#374151", border: "#9ca3af", note: "Practice closed" },
                            "Not a Qualifying Lead": { bg: "#e5e7eb", fg: "#374151", border: "#9ca3af", note: "Outside ICP (e.g. pharmacy)" },
                            "Hot":                 { bg: "#fce7f3", fg: "#9d174d", border: "#f9a8d4", note: "High-intent lead" },
                          };
                          const surface = tags.filter(t => SURFACE[t]);
                          if (surface.length === 0 && !lead.do_not_call) return null;
                          return (
                            <div data-testid="precall-flag-bar" style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                              {lead.do_not_call && (
                                <span data-testid="precall-flag-dnc" style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 999, background: "#7f1d1d", color: "#fff", fontSize: 10, fontWeight: 700, letterSpacing: "0.04em" }}>
                                  ⚠ DO NOT CALL{lead.dnc_reason ? ` · ${String(lead.dnc_reason).replace(/^tag_/,"").replace(/_/g," ")}` : ""}
                                </span>
                              )}
                              {surface.map((t) => {
                                const s = SURFACE[t];
                                return (
                                  <span
                                    key={t}
                                    data-testid={`precall-flag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                    title={s.note}
                                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 999, background: s.bg, border: `0.5px solid ${s.border}`, fontSize: 10, fontWeight: 600, color: s.fg }}
                                  >
                                    {t} · {s.note}
                                  </span>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    {callActive && (
                      <span data-testid="call-timer" style={{ fontSize: 20, fontWeight: 500, color: "#E24B4A", fontFamily: "monospace", fontVariantNumeric: "tabular-nums", letterSpacing: 1 }}>{fmtTimer(callTimer)}</span>
                    )}
                  </div>
                  {/* Live Sentiment Bar — AI-detected per spec 2A */}
                  {callActive && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }} data-testid="sentiment-bar">
                      <span style={{ fontSize: 10, color: "#8BA5C0" }}>AI sentiment:</span>
                      {["negative","neutral","positive"].map(s => {
                        const active = sentiment === s;
                        return (
                          <span key={s} data-testid={`sentiment-pill-${s}`}
                            style={{ padding: "3px 12px", borderRadius: 12, fontSize: 10, border: active ? "1px solid rgba(255,255,255,0.4)" : "1px solid transparent", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "var(--font)",
                              background: "rgba(255,255,255,0.06)",
                              color: active ? "#fff" : "#8BA5C0",
                              opacity: active ? 1 : 0.5,
                            }}>
                            {active && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />}
                            {s === "positive" ? "Positive" : s === "negative" ? "Negative" : "Neutral"}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Section 2B — Control Bar below header */}
                {callActive && (
                  <div data-testid="call-controls-bar" style={{ background: "#fff", borderBottom: "0.5px solid #eee", borderLeft: "1px solid #eee", borderRight: "1px solid #eee", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
                    <button data-testid="ctrl-mute" onClick={() => setMuted(!muted)} title={muted ? "Unmute" : "Mute"}
                      style={{ width: 38, height: 38, borderRadius: "50%", background: muted ? "#FAEEDA" : "#fff", border: muted ? "0.5px solid #FAC775" : "0.5px solid #ddd", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {muted ? <MicOff style={{ width: 15, height: 15, color: "#854F0B" }} /> : <Mic style={{ width: 15, height: 15, color: "#444" }} />}
                    </button>
                    <button data-testid="ctrl-hold" onClick={() => toast.info(muted ? "On hold (demo)" : "Hold toggled (demo)")} title="Hold"
                      style={{ width: 38, height: 38, borderRadius: "50%", background: "#fff", border: "0.5px solid #ddd", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Pause style={{ width: 15, height: 15, color: "#444" }} />
                    </button>
                    {/* Iter 185 (Feb 2026) — Transfer now opens the real
                        picker instead of a toast placeholder. */}
                    <button data-testid="ctrl-transfer" onClick={() => { setClassicConfOpen(false); setClassicXferOpen(v => !v); }} title="Transfer"
                      style={{ width: 38, height: 38, borderRadius: "50%", background: classicXferOpen ? "#EFF6FF" : "#fff", border: classicXferOpen ? "0.5px solid #93C5FD" : "0.5px solid #ddd", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <ArrowRightLeft style={{ width: 15, height: 15, color: classicXferOpen ? "#1D4ED8" : "#444" }} />
                    </button>
                    {/* Iter 185 — new Conference button. */}
                    <button data-testid="ctrl-conference" onClick={() => { setClassicXferOpen(false); setClassicConfOpen(v => !v); }} title="Conference"
                      style={{ width: 38, height: 38, borderRadius: "50%", background: classicConfOpen ? "#ECFDF5" : "#fff", border: classicConfOpen ? "0.5px solid #6EE7B7" : "0.5px solid #ddd", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Users style={{ width: 15, height: 15, color: classicConfOpen ? "#047857" : "#444" }} />
                    </button>
                    {/* Popovers — anchored to the control bar. */}
                    <TransferConferencePicker
                      mode="transfer"
                      open={classicXferOpen}
                      onClose={() => setClassicXferOpen(false)}
                      fromCallId={classicCallId}
                      leadId={selectedLead?.id}
                      onAction={(action, target) => {
                        window.dispatchEvent(new CustomEvent("webphone:control", {
                          detail: { action, target: target.extension || target.phone },
                        }));
                      }}
                    />
                    <TransferConferencePicker
                      mode="conference"
                      open={classicConfOpen}
                      onClose={() => setClassicConfOpen(false)}
                      fromCallId={classicCallId}
                      leadId={selectedLead?.id}
                      onAction={(action, target, resp) => {
                        if (resp?.mode === "fallback") {
                          window.dispatchEvent(new CustomEvent("webphone:control", {
                            detail: { action: "conference-add", target: target.extension || target.phone, fallback: true },
                          }));
                        }
                      }}
                    />
                    <button data-testid="ctrl-keypad" onClick={() => toast.info("Keypad (DTMF) — opens mini-overlay")} title="Keypad"
                      style={{ width: 38, height: 38, borderRadius: "50%", background: "#fff", border: "0.5px solid #ddd", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Grid3X3 style={{ width: 15, height: 15, color: "#444" }} />
                    </button>
                    <div style={{ flex: 1 }} />
                    <button data-testid="end-call-btn" onClick={endCall}
                      style={{ background: "#E24B4A", color: "#fff", border: "none", borderRadius: 20, padding: "8px 20px", fontSize: 13, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "var(--font)" }}>
                      <PhoneOff style={{ width: 14, height: 14 }} /> End call
                    </button>
                  </div>
                )}

                <div style={{ height: 12 }} />

                {/* Real mode retains the legacy collapsible contact capture UX */}
                {contactCaptureOpen && (
                  <div data-testid="contact-capture-panel" className="card" style={{ padding: "14px 16px", marginBottom: 12, borderLeft: "3px solid var(--blue)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Contact name</label>
                        <input data-testid="capture-name" value={contactCaptureForm.contact_name} onChange={e => setContactCaptureForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="Who are you actually speaking with?" style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontFamily: "var(--font)", marginTop: 3 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Role / title</label>
                        <input data-testid="capture-role" value={contactCaptureForm.role} onChange={e => setContactCaptureForm(f => ({ ...f, role: e.target.value }))} placeholder="e.g. Practice Manager" style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontFamily: "var(--font)", marginTop: 3 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Phone</label>
                        <input data-testid="capture-phone" value={contactCaptureForm.phone} onChange={e => setContactCaptureForm(f => ({ ...f, phone: e.target.value }))} style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontFamily: "var(--font)", marginTop: 3 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Email</label>
                        <input data-testid="capture-email" value={contactCaptureForm.email} onChange={e => setContactCaptureForm(f => ({ ...f, email: e.target.value }))} style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontFamily: "var(--font)", marginTop: 3 }} />
                      </div>
                    </div>
                    {newContactPrompt && (
                      <div data-testid="new-contact-prompt" style={{ background: "#FEF3C7", border: "0.5px solid #FCD34D", borderRadius: 8, padding: "8px 10px", fontSize: 11, color: "#92400E", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                        <AlertTriangle style={{ width: 12, height: 12, flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>
                          <strong>{newContactPrompt.name}</strong> is a new contact for this lead.
                        </span>
                        <button data-testid="confirm-new-contact" onClick={() => saveContactCapture({ create_contact: true })} style={{ background: "#92400E", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)" }}>Add as new contact</button>
                        <button onClick={() => setNewContactPrompt(null)} style={{ background: "none", border: "0.5px solid #92400E", color: "#92400E", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)" }}>Dismiss</button>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button onClick={() => setContactCaptureOpen(false)} className="btn" style={{ fontSize: 11 }}>Cancel</button>
                      <button data-testid="save-contact-capture" onClick={() => saveContactCapture()} disabled={!contactCaptureForm.contact_name.trim()} className="btn btn-primary" style={{ fontSize: 11, opacity: contactCaptureForm.contact_name.trim() ? 1 : 0.5 }}>Save contact</button>
                    </div>
                  </div>
                )}

                {/* Expandable Script Display — Iter 183 (Feb 2026):
                    if any script (own or published) is available, render
                    it as before. Otherwise fall back to the vetted 7-
                    category `TalkTrackPanel` so ALL roles see a Talk
                    Track during an active call. Kill-switch:
                    TALK_TRACK_FALLBACK_ENABLED=false → old behaviour. */}
                {scripts.length > 0 ? (
                <div className="card" style={{ padding: "16px 18px" }} data-testid="call-script-panel">
                  <div className="card-title" style={{ marginBottom: 12 }}>Call Script — {scripts[0].name}</div>
                  {scripts[0].sections.map((sec, i) => {
                    const isActive = activeScriptIdx === i;
                    const isOpen = expandedSection === i;
                    return (
                      <div key={i} style={{ marginBottom: 4 }}>
                        <div onClick={() => { setExpandedSection(isOpen ? -1 : i); setActiveScriptIdx(i); }}
                          style={{ padding: "10px 14px", background: isActive ? "#E6F1FB" : "#f9f9f9", borderRadius: isOpen ? "8px 8px 0 0" : 8, borderLeft: isActive ? "3px solid #2563eb" : "3px solid transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                          data-testid={`script-section-${i}`}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ width: 20, height: 20, borderRadius: "50%", background: isActive ? "#2563eb" : "#eee", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, color: isActive ? "#fff" : "#888" }}>{i + 1}</div>
                            <span style={{ fontSize: 12, fontWeight: 500 }}>{sec.title}</span>
                          </div>
                          <ChevronDown style={{ width: 12, height: 12, color: "#888", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                        </div>
                        {isOpen && (
                          <div style={{ padding: "10px 14px 14px 40px", background: "#fafafa", borderRadius: "0 0 8px 8px", borderLeft: isActive ? "3px solid #2563eb" : "3px solid transparent" }} data-testid={`script-lines-${i}`}>
                            {sec.lines.map((line, j) => (
                              <div key={j} style={{ fontSize: 12, color: line.startsWith('"') ? "#1b365D" : "#888", lineHeight: 1.7, marginBottom: 4, fontStyle: line.startsWith('"') ? "italic" : "normal", paddingLeft: line.startsWith('"') ? 0 : 8, borderLeft: line.startsWith('"') ? "2px solid #2563eb" : "none", paddingLeft: line.startsWith('"') ? 8 : 0 }}>
                                {line}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                ) : (
                <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="talk-track-fallback">
                  <div className="card-title" style={{ padding: "16px 18px 4px", margin: 0 }}>Talk Track</div>
                  <div style={{ padding: "0 4px 4px" }}>
                    <TalkTrackPanel />
                  </div>
                </div>
                )}

                {/* Learning Paths — Campaign-backed (demo + real).
                   Binds to the backend LEARNING_COURSES via the campaign's
                   `course_id`. Chapters preview + smart search (billing
                   codes, eligibility, program requirements). Clicking a
                   chapter deep-links to /resources?tab=learning&course=…
                   so the same Learning Paths tab opens at that chapter. */}
                {(() => {
                  const activeCampaign = campaigns.find(c => c.id === (effectiveSelectedLead || selectedLead).campaign_id);
                  if (!activeCampaign?.course_id) {
                    return (
                      <div className="card" style={{ padding: "14px 18px", marginTop: 12 }} data-testid="learning-paths">
                        <div className="card-title" style={{ margin: 0, marginBottom: 8 }}>Learning Paths</div>
                        <div style={{ fontSize: 11, color: "#888" }}>No learning path assigned to this campaign yet.</div>
                      </div>
                    );
                  }
                  return (
                    <CampaignLearningPathV2
                      campaign={activeCampaign}
                      apiBase={process.env.REACT_APP_BACKEND_URL}
                      authToken={token}
                      onNavigateToResources={({ courseId, chapterId }) => {
                        const qs = new URLSearchParams({ tab: "learning", course: courseId });
                        if (chapterId) qs.set("chapter", chapterId);
                        navigate(`/resources?${qs.toString()}`);
                      }}
                      onAttachSlide={(slide) => {
                        setEmailComposerLead({ lead: effectiveSelectedLead || selectedLead, prefillSlide: slide });
                      }}
                    />
                  );
                })()}
              </div>

              {/* AI Co-Pilot Sidebar (3.7) */}
              <div>
                <div style={{ background: "#0F2942", borderRadius: 10, padding: "14px 16px", marginBottom: 12 }} data-testid="ai-copilot">
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                    <Brain style={{ width: 12, height: 12, color: "#93C5FD" }} />
                    <span style={{ fontSize: 12, fontWeight: 500, color: "#fff" }}>AI Co-Pilot</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#CBD5E0", lineHeight: 1.6, marginBottom: 8 }}>
                    <strong style={{ color: "#93C5FD" }}>Lead Brief:</strong> {selectedLead.practice} — {selectedLead.reason}
                  </div>
                  <div style={{ fontSize: 11, color: "#CBD5E0", lineHeight: 1.6, marginBottom: 8 }}>
                    <strong style={{ color: "#FFB84D" }}>Suggested opener:</strong> "Hi {selectedLead.name.split(" ")[0]}, I'm calling from Enable Healthcare. I noticed {selectedLead.priority === "hot" ? "you recently requested a demo" : "it's been a while since we connected"} — do you have a moment?"
                  </div>
                  {callActive && (
                    <div style={{ fontSize: 11, color: "#CBD5E0", lineHeight: 1.6, padding: "8px 10px", background: "rgba(255,255,255,0.06)", borderRadius: 8, marginBottom: 8 }}>
                      <strong style={{ color: "#34c759" }}>Live tip:</strong> {sentiment === "positive" ? "Prospect sounds engaged — transition to demo scheduling" : sentiment === "negative" ? "Resistance detected — pivot to discovery questions" : "Neutral tone — ask about current pain points to build rapport"}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    <button onClick={() => toast.info("Objection handler: 'I understand budget is a concern. Our ROI calculator shows...'")} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 10, background: "rgba(255,255,255,0.08)", color: "#93C5FD", border: "0.5px solid rgba(255,255,255,0.15)", cursor: "pointer", fontFamily: "var(--font)" }}>Handle objection</button>
                    <button onClick={() => toast.info("Competitor comparison loaded...")} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 10, background: "rgba(255,255,255,0.08)", color: "#93C5FD", border: "0.5px solid rgba(255,255,255,0.15)", cursor: "pointer", fontFamily: "var(--font)" }}>Compare competitors</button>
                    <button onClick={() => toast.info("ROI calculator: 30% reduction in admin time...")} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 10, background: "rgba(255,255,255,0.08)", color: "#93C5FD", border: "0.5px solid rgba(255,255,255,0.15)", cursor: "pointer", fontFamily: "var(--font)" }}>ROI calculator</button>
                  </div>
                </div>
                <div className="card" style={{ padding: "12px 14px" }} data-testid="quick-notes-card">
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", flex: 1 }}>Quick notes</div>
                    {noteSavedFlash && <span data-testid="note-saved-flash" style={{ fontSize: 9, color: "var(--green)", fontWeight: 600 }}>✓ Saved to timeline</span>}
                  </div>
                  <textarea ref={notesTextareaRef} value={callNotes} onChange={e => setCallNotes(e.target.value)} placeholder="Type call notes... (⌘+S / Ctrl+S to save)" rows={4} data-testid="quick-notes-textarea" style={{ width: "100%", border: "0.5px solid #eee", borderRadius: 8, padding: "8px 10px", fontSize: 12, fontFamily: "var(--font)", resize: "none" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <span style={{ fontSize: 9, color: "#aaa", flex: 1 }}>Shortcut: ⌘+S / Ctrl+S while focused</span>
                    <button onClick={saveNote} disabled={!callNotes.trim()} className="btn btn-primary" data-testid="save-note-btn" style={{ fontSize: 11, padding: "5px 12px", opacity: callNotes.trim() ? 1 : 0.45, cursor: callNotes.trim() ? "pointer" : "not-allowed" }}>
                      Save note
                    </button>
                  </div>
                  {noteLog.length > 0 && (
                    <div data-testid="note-log" style={{ marginTop: 10, borderTop: "0.5px solid #eee", paddingTop: 8, display: "flex", flexDirection: "column", gap: 6, maxHeight: 140, overflowY: "auto" }}>
                      {noteLog.slice(0, 3).map((n, i) => (
                        <div key={n.id || i} data-testid={`note-log-row-${i}`} style={{ fontSize: 10, color: "#555", background: "#fafaf9", borderRadius: 6, padding: "4px 8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                            <span style={{ fontSize: 9, color: "#888" }}>{n.created_at ? new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                            <span style={{ fontSize: 9, color: "#aaa" }}>· {n.user_name || "you"}</span>
                          </div>
                          <div style={{ whiteSpace: "pre-wrap" }}>{n.text}</div>
                        </div>
                      ))}
                      {noteLog.length > 3 && <div style={{ fontSize: 9, color: "#aaa", textAlign: "center" }}>+{noteLog.length - 3} more in timeline</div>}
                    </div>
                  )}
                </div>
                {/* Section 2H — In-browser RC Softphone + AI Live Coach. */}
                <RCWebPhone
                  key={`webphone-${selectedLead?.id || "idle"}`}
                  prefillNumber={selectedLead?.phone || ""}
                />
                <LiveCoachPanel
                  active={callActive}
                  lead={effectiveSelectedLead || selectedLead}
                  campaignFocus={null}
                  externalId={`dialer-${selectedLead?.id || "anon"}-${callTimer || 0}`}
                  onSentimentChange={(s) => setSentiment(s)}
                  onTranscriptUpdate={(t) => setLastCallTranscript(t)}
                  onFieldUpdates={(updates) => {
                    if (!updates?.length) return;
                    setDispForm((f) => {
                      const next = { ...f };
                      updates.forEach((u) => { if (u.field && u.value) next[u.field] = u.value; });
                      return next;
                    });
                  }}
                />

                {/* Demo-only — Compose email card (Microsoft 365 mock).
                   Opens a side drawer that auto-suggests recipients from the
                   lead's contacts list and lets the rep attach resources
                   or learning-path slides. */}
              </div>
            </div>
          )}

          {/* Post-Call — Detailed Disposition (streamlined from spreadsheet) */}
          {agentView === "postcall" && selectedLead && (
            <div style={{ maxWidth: 760, margin: "0 auto" }}>
              <div className="card" style={{ padding: "20px 24px" }} data-testid="postcall-screen">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>Post-call log</div>
                    <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{selectedLead.name} · {selectedLead.practice} · {fmtTimer(callTimer)}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "#EEEDFE", color: "#3C3489" }}>AI pre-filled</span>
                    <Sparkles style={{ width: 12, height: 12, color: "#3C3489" }} />
                  </div>
                </div>

                {/* AI Summary */}
                <div style={{ background: "#0F2942", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <Sparkles style={{ width: 12, height: 12, color: "#93C5FD" }} />
                    <span style={{ fontSize: 12, fontWeight: 500, color: "#fff" }}>AI Call Analysis</span>
                    <span style={{ fontSize: 9, color: "#8BA5C0", marginLeft: "auto" }}>Fields pre-filled from call · Review & adjust</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.6 }}>
                    {callTimer > 120 ? `${selectedLead.name} showed strong interest in EHR migration for ${selectedLead.practice}. Key concerns: data migration timeline and staff training. Prospect manages ${selectedLead.practice} with current system pain around billing integrations. Recommended: schedule technical deep-dive demo within 1 week. Decision timeline: 1-3 months.`
                     : callTimer > 30 ? `Brief but productive conversation with ${selectedLead.name}. Prospect was receptive but busy. Interest level appears medium. Recommend callback in 2-3 days with a prep email covering key value propositions for ${selectedLead.practice}.`
                     : `Very brief call (${fmtTimer(callTimer)}). Likely voicemail or quick disconnect. Suggest retrying at a different time or switching to email outreach for ${selectedLead.practice}.`}
                  </div>
                </div>

                {/* Section tabs */}
                <div style={{ display: "flex", gap: 0, borderBottom: "0.5px solid #eee", marginBottom: 16 }}>
                  {[{ id: "outcome", label: "Outcome" },{ id: "qualification", label: "Qualification" },{ id: "practice", label: "Practice" },{ id: "next", label: "Next Steps" },{ id: "notes", label: "Notes" }].map(t => (
                    <button key={t.id} onClick={() => setDispSection(t.id)} data-testid={`disp-tab-${t.id}`}
                      style={{ padding: "8px 16px", fontSize: 11, fontWeight: dispSection === t.id ? 600 : 400, cursor: "pointer", background: "none", border: "none", fontFamily: "var(--font)", color: dispSection === t.id ? "var(--blue)" : "var(--text-tertiary)", borderBottom: dispSection === t.id ? "2px solid var(--blue)" : "2px solid transparent" }}>{t.label}</button>
                  ))}
                </div>

                {/* Section: Outcome */}
                {dispSection === "outcome" && (<>
                  <div style={{ ...SL, marginBottom: 8 }}>Call disposition *</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginBottom: 16 }}>
                    {DISPOSITIONS.map(d => (
                      <button key={d.id} onClick={() => setSelectedDisposition(d.id)} data-testid={`disposition-${d.id}`}
                        style={{ padding: "10px 8px", borderRadius: 8, border: selectedDisposition === d.id ? `2px solid ${d.color}` : "1px solid #eee", background: selectedDisposition === d.id ? `${d.color}15` : "#fff", cursor: "pointer", fontFamily: "var(--font)", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: `${d.color}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: d.color }} />
                        </div>
                        <span style={{ fontSize: 10, fontWeight: selectedDisposition === d.id ? 600 : 400, color: selectedDisposition === d.id ? d.color : "#888", textAlign: "center" }}>{d.label}</span>
                      </button>
                    ))}
                  </div>
                  {selectedDisposition === "meeting_booked" && (
                    <div style={{ background: "#DAFBE8", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#1D9E75" }}>Demo will be scheduled</div>
                      <button onClick={() => navigate("/demo-bookings")} style={{ fontSize: 11, color: "#2563eb", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>Go to Demo Bookings &rarr;</button>
                    </div>
                  )}
                </>)}

                {/* Section: Lead Qualification */}
                {dispSection === "qualification" && (<>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Interest level</label>
                      <select className="input" value={dispForm.interestLevel} onChange={e => setDispForm(f => ({ ...f, interestLevel: e.target.value }))} data-testid="disp-interest">
                        <option value="">Select...</option><option>Low</option><option>Medium</option><option>High</option><option>Very High</option>
                      </select></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Overall sentiment</label>
                      <select className="input" value={dispForm.sentiment} onChange={e => setDispForm(f => ({ ...f, sentiment: e.target.value }))}>
                        <option value="positive">Positive</option><option value="neutral">Neutral</option><option value="negative">Negative</option>
                      </select></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Decision timeline</label>
                      <select className="input" value={dispForm.decisionTimeline} onChange={e => setDispForm(f => ({ ...f, decisionTimeline: e.target.value }))}>
                        <option value="">Select...</option><option>Immediate</option><option>1-3 Months</option><option>3-6 Months</option><option>6-12 Months</option><option>12+ Months</option><option>Uncertain</option>
                      </select></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Budget range</label>
                      <select className="input" value={dispForm.budgetRange} onChange={e => setDispForm(f => ({ ...f, budgetRange: e.target.value }))}>
                        <option value="">Select...</option><option>$0 - $1,000</option><option>$1,000 - $5,000</option><option>$5,000 - $10,000</option><option>$10,000 - $25,000</option><option>$25,000 - $50,000</option><option>$50,000+</option><option>Undisclosed</option>
                      </select></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Pricing fit</label>
                      <select className="input" value={dispForm.pricingFit} onChange={e => setDispForm(f => ({ ...f, pricingFit: e.target.value }))}>
                        <option value="">Select...</option><option>Good Fit</option><option>Needs Adjustment</option><option>Too High</option><option>Too Low</option><option>Not Discussed</option>
                      </select></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Engagement level</label>
                      <select className="input" value={dispForm.engagementLevel} onChange={e => setDispForm(f => ({ ...f, engagementLevel: e.target.value }))}>
                        <option value="">Select...</option><option>Low</option><option>Medium</option><option>High</option><option>Very High</option>
                      </select></div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f9f9f9", borderRadius: 8, marginBottom: 12 }}>
                    <input type="checkbox" checked={dispForm.decisionMakers} onChange={e => setDispForm(f => ({ ...f, decisionMakers: e.target.checked }))} style={{ width: 14, height: 14 }} />
                    <label style={{ fontSize: 12 }}>Key decision makers identified during call</label>
                  </div>
                </>)}

                {/* Section: Practice Profile */}
                {dispSection === "practice" && (<>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Practice type</label>
                      <select className="input" value={dispForm.practiceType} onChange={e => setDispForm(f => ({ ...f, practiceType: e.target.value }))}>
                        <option value="">Select...</option><option>Medical</option><option>Dental</option><option>Specialty</option><option>Other</option>
                      </select></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Specialty</label>
                      <input className="input" value={dispForm.specialty} onChange={e => setDispForm(f => ({ ...f, specialty: e.target.value }))} placeholder="e.g. Cardiology, Pediatrics..." /></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Number of providers</label>
                      <input className="input" type="number" value={dispForm.numProviders} onChange={e => setDispForm(f => ({ ...f, numProviders: e.target.value }))} placeholder="e.g. 5" /></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Current EHR system</label>
                      <input className="input" value={dispForm.currentEhr} onChange={e => setDispForm(f => ({ ...f, currentEhr: e.target.value }))} placeholder="e.g. Epic, Cerner, athena..." /></div>
                  </div>
                  <div style={{ marginBottom: 12 }}><label style={{ ...SL, display: "block", marginBottom: 4 }}>Pain points discussed</label>
                    <textarea className="input" rows={2} value={dispForm.painPoints} onChange={e => setDispForm(f => ({ ...f, painPoints: e.target.value }))} placeholder="Key frustrations or challenges mentioned..." style={{ resize: "none" }} /></div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Feature interest</label>
                      <input className="input" value={dispForm.featureInterest} onChange={e => setDispForm(f => ({ ...f, featureInterest: e.target.value }))} placeholder="e.g. Billing, Scheduling..." /></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Integration requirements</label>
                      <input className="input" value={dispForm.integrationReqs} onChange={e => setDispForm(f => ({ ...f, integrationReqs: e.target.value }))} placeholder="e.g. Lab systems, pharmacy..." /></div>
                  </div>
                </>)}

                {/* Section: Next Steps */}
                {dispSection === "next" && (<>
                  {(dispErrors.nextAction || dispErrors.dueDate) && (
                    <div data-testid="disp-next-errors" style={{ padding: "10px 12px", background: "#fff1f0", border: "1px solid #ff3b30", borderRadius: 8, marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#ff3b30", marginBottom: 4 }}>Required fields for this disposition:</div>
                      {dispErrors.nextAction && <div style={{ fontSize: 11, color: "#ff3b30" }}>• {dispErrors.nextAction}</div>}
                      {dispErrors.dueDate && <div style={{ fontSize: 11, color: "#ff3b30" }}>• {dispErrors.dueDate}</div>}
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>
                      Next action {selectedDisposition && FOLLOWUP_REQUIRED_DISPOSITIONS.has(selectedDisposition) && <span style={{ color: "#ff3b30" }}>*</span>}
                    </label>
                      <input className="input" data-testid="disp-next-action" value={dispForm.nextAction} onChange={e => { setDispForm(f => ({ ...f, nextAction: e.target.value })); setDispErrors(er => ({ ...er, nextAction: undefined })); }} placeholder="e.g. Schedule demo, Send proposal..." style={dispErrors.nextAction ? { borderColor: "#ff3b30" } : {}} /></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Action owner</label>
                      <input className="input" value={dispForm.nextActionOwner} onChange={e => setDispForm(f => ({ ...f, nextActionOwner: e.target.value }))} placeholder="Rep name..." /></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>
                      Follow-up date/time {selectedDisposition && FOLLOWUP_REQUIRED_DISPOSITIONS.has(selectedDisposition) && <span style={{ color: "#ff3b30" }}>*</span>}
                    </label>
                      <input className="input" type="datetime-local" data-testid="disp-due-date" value={dispForm.dueDate} onChange={e => { setDispForm(f => ({ ...f, dueDate: e.target.value })); setDispErrors(er => ({ ...er, dueDate: undefined })); }} style={dispErrors.dueDate ? { borderColor: "#ff3b30" } : {}} /></div>
                    <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Expected close date</label>
                      <input className="input" type="date" value={dispForm.expectedClose} onChange={e => setDispForm(f => ({ ...f, expectedClose: e.target.value }))} /></div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ ...SL, display: "block", marginBottom: 6 }}>Lead rating</label>
                    <div style={{ display: "flex", gap: 4 }}>
                      {[1,2,3,4,5].map(n => (
                        <button key={n} onClick={() => setDispForm(f => ({ ...f, leadRating: n }))}
                          style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #eee", background: dispForm.leadRating >= n ? "#FFB84D" : "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} data-testid={`rating-${n}`}>
                          <Star style={{ width: 14, height: 14, color: dispForm.leadRating >= n ? "#fff" : "#ddd", fill: dispForm.leadRating >= n ? "#fff" : "none" }} />
                        </button>
                      ))}
                      <span style={{ fontSize: 11, color: "#888", marginLeft: 8, alignSelf: "center" }}>{dispForm.leadRating > 0 ? `${dispForm.leadRating}/5` : "Not rated"}</span>
                    </div>
                  </div>
                  <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Key value proposition discussed</label>
                    <textarea className="input" rows={2} value={dispForm.keyValueProp} onChange={e => setDispForm(f => ({ ...f, keyValueProp: e.target.value }))} placeholder="What resonated most with the prospect..." style={{ resize: "none" }} /></div>
                </>)}

                {/* Section: Notes */}
                {dispSection === "notes" && (<>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ ...SL, display: "block", marginBottom: 4 }}>Call notes</label>
                    <textarea className="input" rows={4} value={callNotes} onChange={e => setCallNotes(e.target.value)} placeholder="Detailed call notes..." style={{ resize: "none" }} />
                  </div>
                  <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Migration needs</label>
                    <textarea className="input" rows={2} value={dispForm.migrationNeeds} onChange={e => setDispForm(f => ({ ...f, migrationNeeds: e.target.value }))} placeholder="Data migration, timeline, special requirements..." style={{ resize: "none" }} /></div>
                </>)}

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20, paddingTop: 16, borderTop: "0.5px solid #eee", gap: 8, flexWrap: "wrap" }}>
                  {/* Iter 179.9 (Tatiana #3dbe9163) — explicit "Back" button so reps
                      who launched from Tasks or a Lead always have a one-click route
                      back, even without saving. Falls back to "Skip & return to
                      queue" when the call wasn't launched from a known source. */}
                  {returnTarget ? (
                    <button
                      onClick={() => { try { window.location.assign(returnTarget.path); } catch (_) { navigate(returnTarget.path); } }}
                      className="btn"
                      data-testid="postcall-back-btn"
                      style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      ← {returnTarget.label}
                    </button>
                  ) : (
                    <button onClick={() => { setSelectedLead(null); setSelectedDisposition(null); setCallNotes(""); setDispForm({ interestLevel: "", sentiment: "neutral", decisionTimeline: "", budgetRange: "", pricingFit: "", practiceType: "", specialty: "", numProviders: "", currentEhr: "", painPoints: "", nextAction: "", nextActionOwner: "", dueDate: "", leadRating: 0, expectedClose: "", keyValueProp: "", integrationReqs: "", migrationNeeds: "", decisionMakers: false, featureInterest: "", engagementLevel: "" }); setAgentView("queue"); }} className="btn" style={{ fontSize: 12 }}>Skip & return to queue</button>
                  )}
                  {/* Slice E — Task preview box */}
                  {postCallPreview ? (
                    <div data-testid="postcall-task-preview" style={{ flex: 1, margin: "0 12px", background: "#F0FDF4", border: "0.5px solid #86EFAC", borderRadius: 10, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                      <Check style={{ width: 14, height: 14, color: "var(--green)", flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: "#166534", letterSpacing: 0.3, textTransform: "uppercase" }}>Auto-task preview</div>
                        <div style={{ fontSize: 11, color: "#166534" }}>
                          <strong>{postCallPreview.title}</strong>
                          <span style={{ opacity: 0.7 }}> · {postCallPreview.priority} · due {postCallPreview.due_date}</span>
                        </div>
                      </div>
                    </div>
                  ) : <div style={{ flex: 1 }} />}
                  <button
                    onClick={() => setFollowUpOpen(true)}
                    className="btn"
                    style={{ fontSize: 12, background: "#fff", border: "0.5px solid #5B6CFF", color: "#5B6CFF", marginRight: 8 }}
                    data-testid="draft-follow-up-email-btn"
                  >
                    📧 Draft follow-up email
                  </button>
                  <button onClick={submitDisposition} className="btn btn-primary" style={{ fontSize: 12 }} data-testid="submit-disposition-btn">
                    Submit & update lead profile
                  </button>
                </div>
              </div>
            </div>
          )}
        </>)}
      </div>

      {/* Post-call AI-drafted follow-up email modal */}
      <FollowUpEmailModal
        open={followUpOpen}
        onClose={() => setFollowUpOpen(false)}
        transcript={lastCallTranscript}
        lead={effectiveSelectedLead || selectedLead}
        disposition={dispForm}
      />

      {/* Create Campaign Modal (3.2 wizard) */}
      {/* ═══ Slice D — Script Editor Modal ═══ */}
      {scriptEditor && (
        <div data-testid="script-editor-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 24, width: "95%", maxWidth: 760, maxHeight: "90vh", overflow: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{scriptEditor.id ? "Edit Script" : "New Script"}</h2>
              <button onClick={() => setScriptEditor(null)} data-testid="script-editor-close" style={{ background: "none", border: "none", cursor: "pointer" }}><X style={{ width: 18, height: 18 }} /></button>
            </div>

            {/* ── Section 10A — AI generation panel ── */}
            <div data-testid="script-ai-panel" style={{ background: "#EEEDFE", border: "0.5px solid #CECBF6", borderRadius: 10, padding: aiScriptGen.open ? "14px 16px" : "8px 12px", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#3C3489", display: "flex", alignItems: "center", gap: 6 }}>
                  <Sparkles style={{ width: 14, height: 14 }} /> Generate script with AI
                </div>
                <button onClick={() => setAiScriptGen(s => ({ ...s, open: !s.open }))} data-testid="script-ai-toggle" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "#888" }}>
                  {aiScriptGen.open ? "or build manually ↓" : "expand AI panel ↑"}
                </button>
              </div>
              {aiScriptGen.open && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: "#3C3489", textTransform: "uppercase", letterSpacing: 0.3 }}>Campaign type</label>
                      <select className="input" data-testid="script-ai-campaign-type" value={aiScriptGen.campaign_type} onChange={e => setAiScriptGen(s => ({ ...s, campaign_type: e.target.value }))} style={{ fontSize: 12 }}>
                        {["Outbound cold call","Warm follow-up","Re-engagement","Inbound callback","Demo booking","Post-demo follow-up","VM Call Back","Custom"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: "#3C3489", textTransform: "uppercase", letterSpacing: 0.3 }}>Target specialty</label>
                      <select className="input" data-testid="script-ai-specialty" value={aiScriptGen.specialty} onChange={e => setAiScriptGen(s => ({ ...s, specialty: e.target.value }))} style={{ fontSize: 12 }}>
                        <option value="">— Any —</option>
                        {["Family Medicine","Internal Medicine","Geriatrics","Pediatrics","Cardiology","Pulmonology","Nephrology","Endocrinology","Oncology","Gastroenterology","Rheumatology","Neurology"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: "#3C3489", textTransform: "uppercase", letterSpacing: 0.3 }}>Primary product</label>
                      <select className="input" data-testid="script-ai-product" value={aiScriptGen.primary_product} onChange={e => setAiScriptGen(s => ({ ...s, primary_product: e.target.value }))} style={{ fontSize: 12 }}>
                        {["EHR (MDnet)","RCM (RevQ)","CCM","RPM","APCM","AWV","Full platform"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: "#3C3489", textTransform: "uppercase", letterSpacing: 0.3 }}>Call goal</label>
                      <select className="input" data-testid="script-ai-goal" value={aiScriptGen.call_goal} onChange={e => setAiScriptGen(s => ({ ...s, call_goal: e.target.value }))} style={{ fontSize: 12 }}>
                        {["Book a demo","Qualify the lead","Re-engage cold lead","Handle objection","Close / next steps"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: "#3C3489", textTransform: "uppercase", letterSpacing: 0.3 }}>Tone</label>
                      <select className="input" data-testid="script-ai-tone" value={aiScriptGen.tone} onChange={e => setAiScriptGen(s => ({ ...s, tone: e.target.value }))} style={{ fontSize: 12 }}>
                        {["Professional","Conversational","Direct","Consultative"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                  {aiScriptGen.banner && (
                    <div style={{ background: "#FAEEDA", border: "0.5px solid #FAC775", color: "#854F0B", borderRadius: 8, padding: "6px 10px", fontSize: 11 }} data-testid="script-ai-warning">⚠ {aiScriptGen.banner}</div>
                  )}
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button onClick={generateScriptWithAI} disabled={aiScriptGen.loading} data-testid="script-ai-generate-btn" style={{ background: "#3C3489", color: "#fff", border: "none", borderRadius: 8, padding: "7px 18px", fontSize: 13, fontWeight: 500, cursor: aiScriptGen.loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                      {aiScriptGen.loading ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Sparkles style={{ width: 12, height: 12 }} />}
                      {aiScriptGen.loading ? "Generating…" : "Generate script →"}
                    </button>
                    {(scriptEditor.sections || []).length > 0 && (
                      <button onClick={generateScriptWithAI} disabled={aiScriptGen.loading} data-testid="script-ai-regenerate-btn" style={{ background: "none", border: "none", color: "#3C3489", fontSize: 11, cursor: "pointer", textDecoration: "underline" }}>Regenerate →</button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#555" }}>Name</label>
                <input className="input" data-testid="script-editor-name" value={scriptEditor.name || ""} onChange={e => setScriptEditor({ ...scriptEditor, name: e.target.value })} placeholder="e.g. MDNet Discovery v3" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#555" }}>Estimated minutes</label>
                <input type="number" className="input" data-testid="script-editor-mins" value={scriptEditor.estimated_minutes || 0} onChange={e => setScriptEditor({ ...scriptEditor, estimated_minutes: Number(e.target.value) })} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#555" }}>Products (comma)</label>
              <input className="input" data-testid="script-editor-products" value={(scriptEditor.products || []).join(", ")} onChange={e => setScriptEditor({ ...scriptEditor, products: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} placeholder="mdnet, pms" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#555", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Sections</span>
                <button data-testid="script-add-section" onClick={() => setScriptEditor({ ...scriptEditor, sections: [...(scriptEditor.sections || []), { title: "New section", bullets: [""] }] })} style={{ background: "none", border: "0.5px solid #ddd", borderRadius: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)" }}>+ Add section</button>
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                {(scriptEditor.sections || []).map((sec, i) => (
                  <div key={i} style={{ border: "0.5px solid #eee", borderRadius: 8, padding: 10 }} data-testid={`script-section-${i}`}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                      <input className="input" value={sec.title || ""} onChange={e => { const next = [...scriptEditor.sections]; next[i] = { ...sec, title: e.target.value }; setScriptEditor({ ...scriptEditor, sections: next }); }} placeholder="Section title" style={{ flex: 1, fontSize: 12, fontWeight: 600 }} />
                      <button
                        onClick={() => improveSectionWithAI(i)}
                        disabled={aiSectionImprove[i]?.loading}
                        data-testid={`script-section-improve-${i}`}
                        style={{ background: "none", border: "none", color: "#3C3489", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 3, padding: "2px 6px" }}>
                        {aiSectionImprove[i]?.loading ? <Loader2 style={{ width: 11, height: 11, animation: "spin 0.8s linear infinite" }} /> : <Sparkles style={{ width: 11, height: 11 }} />}
                        Improve
                      </button>
                      <button onClick={() => { const next = scriptEditor.sections.filter((_, j) => j !== i); setScriptEditor({ ...scriptEditor, sections: next }); }} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", padding: 4 }}><Trash2 style={{ width: 12, height: 12 }} /></button>
                    </div>
                    <textarea className="input" rows={3} value={(sec.bullets || []).join("\n")} onChange={e => { const next = [...scriptEditor.sections]; next[i] = { ...sec, bullets: e.target.value.split("\n") }; setScriptEditor({ ...scriptEditor, sections: next }); }} placeholder="One bullet per line…" style={{ fontSize: 11, fontFamily: "var(--font)", resize: "vertical", width: "100%" }} />
                    {aiSectionImprove[i]?.suggestion && (
                      <div data-testid={`script-section-suggestion-${i}`} style={{ marginTop: 8, background: "#EEEDFE", border: "0.5px solid #CECBF6", borderRadius: 6, padding: "8px 10px" }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: "#3C3489", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}><Sparkles style={{ width: 10, height: 10 }} /> AI suggestion</div>
                        <pre style={{ fontSize: 12, color: "#3C3489", margin: 0, whiteSpace: "pre-wrap", fontFamily: "var(--font)" }}>{aiSectionImprove[i].suggestion}</pre>
                        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                          <button onClick={() => acceptSectionImprovement(i)} className="btn btn-primary" data-testid={`script-section-accept-${i}`} style={{ fontSize: 11, padding: "3px 10px" }}>Use this version</button>
                          <button onClick={() => setAiSectionImprove(p => ({ ...p, [i]: { loading: false, suggestion: null } }))} className="btn" data-testid={`script-section-reject-${i}`} style={{ fontSize: 11, padding: "3px 10px" }}>Keep original</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14, paddingTop: 14, borderTop: "0.5px solid #eee" }}>
              <button onClick={() => setScriptEditor(null)} className="btn" style={{ fontSize: 12 }}>Cancel</button>
              <button onClick={() => saveScript({ ...scriptEditor })} className="btn btn-primary" style={{ fontSize: 12 }} data-testid="script-editor-save">
                {scriptEditor.id ? "Save changes" : "Create script"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Slice D — Script Preview Drawer ═══ */}
      {scriptPreviewId && (() => {
        const s = scriptsList.find(x => x.id === scriptPreviewId);
        if (!s) return null;
        return (
          <div data-testid="script-preview-drawer" style={{ position: "fixed", inset: 0, zIndex: 99, display: "flex", justifyContent: "flex-end" }}>
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} onClick={() => setScriptPreviewId(null)} />
            <div style={{ position: "relative", width: 480, maxWidth: "92vw", background: "#fff", height: "100%", overflowY: "auto", padding: 20, boxShadow: "-4px 0 20px rgba(0,0,0,0.1)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{s.name}</h3>
                <button onClick={() => setScriptPreviewId(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X style={{ width: 16, height: 16 }} /></button>
              </div>
              <div style={{ fontSize: 10, color: "#888", marginBottom: 16 }}>{s.status} · {s.estimated_minutes || 0} min · {(s.products || []).join(", ")}</div>
              {(s.sections || []).map((sec, i) => (
                <div key={i} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4, color: "#111" }}>{i + 1}. {sec.title || "Section"}</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {(sec.bullets || []).map((b, j) => <li key={j} style={{ fontSize: 12, color: "#333", marginBottom: 3 }}>{b}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );
      })()}


      {showCreateCampaign && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          data-testid="create-campaign-backdrop"
          // Iter 270 (Jul 2026) — do NOT close on backdrop click. Reps
          // were losing multi-step wizard state (basics + audience +
          // script + agents) to a stray mis-click while dragging text
          // or selecting AI-parsed chips. Dismiss is now explicit via
          // the ✕ button or the Escape key (handled below).
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              // Dynamically sized: audience step needs the wide filter rail,
              // every other step gets the compact width. Both clamp to viewport
              // so nothing bleeds off-screen at small widths.
              width: campaignStep === 2 ? "min(1240px, 96vw)" : "min(760px, 96vw)",
              maxHeight: "92vh",
              height: "min(880px, 92vh)",
              display: "flex",
              flexDirection: "column",
              border: "0.5px solid #eee",
              transition: "width 220ms ease",
              overflow: "hidden",
            }}
            data-testid="create-campaign-modal"
          >
            <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 500 }} data-testid="campaign-modal-title">
                {editingCampaignId ? "Edit Campaign" : "New Campaign"} — Step {campaignStep} of 5
              </span>
              <button data-testid="campaign-modal-close" onClick={() => { setShowCreateCampaign(false); resetCampaignModalState(); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#888" }}>&times;</button>
            </div>
            <div style={{ padding: 20, flex: 1, minHeight: 0, overflowY: "auto" }}>
              {/* Step indicator */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, marginBottom: 20 }}>
                {["Basics","Lead list","Script","Agents","Review"].map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center" }}>
                    {i > 0 && <div style={{ width: 24, height: 2, background: campaignStep > i ? "#1D9E75" : "#eee" }} />}
                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: campaignStep > i + 1 ? "#1D9E75" : campaignStep === i + 1 ? "#2563eb" : "#f5f5f5", color: campaignStep >= i + 1 ? "#fff" : "#888", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600 }}>
                      {campaignStep > i + 1 ? <Check style={{ width: 10, height: 10 }} /> : i + 1}
                    </div>
                  </div>
                ))}
              </div>
              {campaignStep === 1 && (<>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ ...SL, display: "block", marginBottom: 4 }}>Campaign name</label>
                  <input
                    className="input"
                    placeholder="e.g. Q2 Practice Outreach"
                    value={campaignNameInput}
                    onChange={e => setCampaignNameInput(e.target.value)}
                    data-testid="campaign-name-input"
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ ...SL, display: "block", marginBottom: 4 }}>Type</label>
                    <select className="input" value={campaignTypeInput} onChange={e => setCampaignTypeInput(e.target.value)} data-testid="campaign-type-select">
                      <option value="outbound">Outbound</option>
                      <option value="inbound_callback">Inbound callback</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ ...SL, display: "block", marginBottom: 4 }}>Priority</label>
                    <select className="input" value={campaignPriorityInput} onChange={e => setCampaignPriorityInput(e.target.value)} data-testid="campaign-priority-select">
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
              </>)}
              {campaignStep === 2 && (
                <CampaignAudienceBuilder
                  campaignFilters={campaignFilters}
                  setFilter={setFilter}
                  toggleArrayFilter={toggleArrayFilter}
                  campaignPresets={campaignPresets}
                  applyPreset={applyPreset}
                  savePresetLocally={savePresetLocally}
                  activePresetId={activePresetId}
                  presetDefaults={CAMPAIGN_PRESET_DEFAULTS}
                  campaignPreview={campaignPreview}
                  onLoadMoreAudience={loadMoreAudiencePage}
                  onLuminaPin={(ids, meta) => { setLuminaPinIds(ids); setLuminaPinMeta(meta); }}
                  onExcludeLead={(leadId) => {
                    // Iter 279 (Jul 2026) — remove a single lead from
                    // the currently-pinned LUMINA audience. Works only
                    // for LUMINA-pinned mode (child hides the button
                    // otherwise). We update BOTH the pin array (so
                    // downstream campaign save carries the trimmed
                    // list) AND the preview's sample+count (so the
                    // right-hand panel updates instantly without
                    // waiting for the debounced audience-preview
                    // refetch).
                    if (!Array.isArray(luminaPinIds)) {
                      toast.info("Exclude is available on AI-pinned audiences only.");
                      return;
                    }
                    const nextIds = luminaPinIds.filter(id => id !== leadId);
                    setLuminaPinIds(nextIds);
                    setCampaignPreview((prev) => {
                      const nextSample = (prev?.sample || []).filter(l => l.id !== leadId);
                      const nextCount = Math.max(0, (Number(prev?.count) || 0) - 1);
                      return { ...(prev || {}), sample: nextSample, count: nextCount };
                    });
                    // Keep the pin meta but reflect the trimmed count
                    // so any downstream "matched N leads" toast stays
                    // truthful if the user re-runs the AI panel.
                    setLuminaPinMeta((prev) => prev ? { ...prev, matched_count: Math.max(0, (prev.matched_count || 0) - 1) } : prev);
                    toast.success("Lead excluded from campaign audience");
                  }}
                />
              )}
              {campaignStep === 3 && (<>
                <div style={{ ...SL, marginBottom: 8 }}>Assign call script</div>
                {[...scriptsList, ...scripts].length === 0 && (
                  <div style={{ fontSize: 11, color: "#888", textAlign: "center", padding: "16px 0", border: "1px dashed var(--border)", borderRadius: 8 }}>No scripts in the library yet.</div>
                )}
                {[...scriptsList, ...scripts].map(s => {
                  const sections = Array.isArray(s.sections) ? s.sections : [];
                  const checked = campaignScriptIdInput === s.id;
                  return (
                    <label key={s.id} data-testid={`campaign-script-option-${s.id}`}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "0.5px solid", borderColor: checked ? "#2563eb" : "#eee", borderLeft: checked ? "3px solid #2563eb" : "3px solid transparent", borderRadius: 8, marginBottom: 6, cursor: "pointer", background: checked ? "#f0f7ff" : "#fff" }}>
                      <input type="radio" name="script" style={{ width: 14, height: 14 }}
                        checked={checked}
                        onChange={() => setCampaignScriptIdInput(s.id)}
                        data-testid={`campaign-script-radio-${s.id}`}
                      />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>{sections.length} sections{s.type ? ` · ${s.type}` : (s.status ? ` · ${s.status}` : "")}</div>
                      </div>
                    </label>
                  );
                })}
              </>)}
              {campaignStep === 4 && (<>
                <div data-testid="campaign-step4" style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Assign agents to this campaign</div>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>Select which agents will call this campaign's leads.</div>
                  <div data-testid="campaign-agent-list" style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto", marginBottom: 12 }}>
                    {agents.length === 0 && (
                      <div style={{ fontSize: 11, color: "#888", textAlign: "center", padding: "16px 0", border: "1px dashed var(--border)", borderRadius: 8 }}>
                        No agents available yet. Invite agents from the Agents tab.
                      </div>
                    )}
                    {agents.map(a => {
                      const selected = campaignStepAgents.includes(a.id);
                      return (
                        <label key={a.id} data-testid={`campaign-agent-option-${a.id}`}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "0.5px solid", borderColor: selected ? "#2563eb" : "#eee", borderLeft: selected ? "3px solid #2563eb" : "3px solid transparent", borderRadius: 8, background: selected ? "#f0f7ff" : "#fff", cursor: "pointer" }}>
                          <input type="checkbox" checked={selected} onChange={() => setCampaignStepAgents(prev => selected ? prev.filter(x => x !== a.id) : [...prev, a.id])} style={{ width: 13, height: 13 }} data-testid={`campaign-agent-check-${a.id}`} />
                          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#E6F1FB", color: "#185FA5", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                            {(a.name || "").split(" ").map(n => n[0]).join("").slice(0, 2)}
                          </span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{a.name}</div>
                            <div style={{ fontSize: 11, color: "#888" }}>{a.role} · {a.campaign ? `On: ${a.campaign}` : "Unassigned"} · {a.callsToday ?? 0} calls today</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
                    <div>
                      <label style={{ ...SL, display: "block", marginBottom: 4 }}>Distribution method</label>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {[{ id: "round_robin", label: "Round robin — evenly distribute leads" }, { id: "fcfs", label: "First come first served — shared pool" }].map(o => (
                          <label key={o.id} data-testid={`distribution-${o.id}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                            <input type="radio" checked={campaignDistribution === o.id} onChange={() => setCampaignDistribution(o.id)} />
                            {o.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label style={{ ...SL, display: "block", marginBottom: 4 }}>Daily target per agent</label>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input data-testid="daily-target-input" type="number" min={1} max={500} value={campaignDailyTarget} onChange={e => setCampaignDailyTarget(parseInt(e.target.value || "0", 10))} className="input" style={{ width: 100 }} />
                        <span style={{ fontSize: 11, color: "#888" }}>calls / day</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>)}
              {campaignStep === 5 && (<>
                <div data-testid="campaign-step5" style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 10, padding: 16, marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Review {editingCampaignId ? "changes" : "& launch"}</div>
                  <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "0.5px solid #f5f5f5" }}>
                    <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>Campaign</div>
                    <div style={{ fontSize: 13 }}>
                      {campaignNameInput || <span style={{ color: "#888" }}>Unnamed</span>} ·{" "}
                      <span style={{ padding: "2px 8px", borderRadius: 10, background: "#EEEDFE", color: "#3C3489", fontSize: 10 }}>{campaignTypeInput}</span>{" "}
                      <span style={{ padding: "2px 8px", borderRadius: 10, background: "#FEF3C7", color: "#92400E", fontSize: 10, marginLeft: 4 }}>{campaignPriorityInput}</span>
                    </div>
                  </div>
                  <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "0.5px solid #f5f5f5" }}>
                    <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>Leads</div>
                    <div style={{ fontSize: 13 }}>{campaignPreview?.count ?? "—"}{campaignPreview?.capped ? "+" : ""} leads match your filters</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                      {campaignFilters.stages?.slice(0, 4).map(s => <span key={s} style={{ padding: "1px 6px", borderRadius: 10, fontSize: 10, background: "#D1FAE5", color: "#065F46" }}>stage: {s}</span>)}
                      {campaignFilters.sources?.slice(0, 4).map(s => <span key={s} style={{ padding: "1px 6px", borderRadius: 10, fontSize: 10, background: "#DBEAFE", color: "#1D4ED8" }}>source: {s}</span>)}
                    </div>
                  </div>
                  <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "0.5px solid #f5f5f5" }}>
                    <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>Script</div>
                    {(() => {
                      const picked = [...scriptsList, ...scripts].find(s => s.id === campaignScriptIdInput);
                      return <div style={{ fontSize: 13 }}>{picked ? picked.name : <span style={{ color: "#888" }}>None selected</span>}</div>;
                    })()}
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>Team setup</div>
                    <div style={{ fontSize: 13 }}>
                      {campaignStepAgents.length} agent{campaignStepAgents.length === 1 ? "" : "s"} · {campaignDistribution === "round_robin" ? "Round robin" : "First come first served"} · {campaignDailyTarget} calls/day
                    </div>
                  </div>
                </div>
              </>)}
            </div>
            <div style={{ padding: "14px 20px", borderTop: "0.5px solid #eee", display: "flex", justifyContent: "space-between", flexShrink: 0, background: "#fafafa" }}>
              {campaignStep > 1 ? <button data-testid="campaign-back-btn" onClick={() => setCampaignStep(campaignStep - 1)} className="btn" style={{ fontSize: 12 }}>Back</button> : <div />}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {/* Iter 167.b — "Save agents only" shortcut on Step 4 in
                    edit mode. Lets admins update the agent roster without
                    clicking through Step 5. Hidden in create mode (no
                    campaign exists yet) and on steps other than 4. */}
                {editingCampaignId && campaignStep === 4 && campaignStepAgents.length > 0 && (
                  <button
                    data-testid="campaign-save-agents-btn"
                    onClick={saveAgentAssignmentsOnly}
                    className="btn"
                    style={{ fontSize: 12, background: "#fff", border: "0.5px solid #2563eb", color: "#2563eb" }}>
                    Save agents & close
                  </button>
                )}
                {campaignStep < 5 ? (
                  <button
                    data-testid="campaign-next-btn"
                    onClick={() => setCampaignStep(campaignStep + 1)}
                    disabled={campaignStep === 4 && campaignStepAgents.length === 0}
                    className="btn btn-primary"
                    style={{ fontSize: 12, opacity: (campaignStep === 4 && campaignStepAgents.length === 0) ? 0.5 : 1 }}>Next</button>
                ) : (
                  <button
                    data-testid={editingCampaignId ? "campaign-save-btn" : "campaign-launch-btn"}
                    onClick={saveOrLaunchCampaign}
                    className="btn btn-primary"
                    style={{ fontSize: 12 }}>
                    {editingCampaignId ? "Save changes" : "Launch Campaign"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

      {showAddContact && (
        <DialerAddContactModal
          onClose={() => setShowAddContact(false)}
          onCreated={(contact) => {
            setShowAddContact(false);
            fetchRealQueue();
            toast.success(`${contact.name} created`);
          }}
        />
      )}
    </div>
  );
}
